import { useState, useEffect, useRef, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDashboardStats,
  getTenants, getTenantById, updateTenantStatus, getTenantUsage, assignSubscription, cancelSubscription,
  getPlans, createPlan, updatePlan, deletePlan,
  getInvoices, getInvoiceById, createInvoice, issueInvoice, cancelInvoice, recordPayment,
  getPayments,
  getAuditLogs,
  PAYMENT_METHOD_TO_API, PAYMENT_METHOD_FROM_API,
} from "./superAdminApi";

// ─── PALETTE & CONSTANTS ──────────────────────────────────────────────────────
const C = {
  bg: "#0d0c0b", surface: "#141312", card: "#1a1918", card2: "#1f1e1c",
  border: "#252320", border2: "#2e2c29",
  accent: "#e8602a", accent2: "#c84a1e",
  green: "#34c97c", yellow: "#f59e0b", blue: "#5b8dee", purple: "#a855f7", red: "#ef4444",
  text1: "#f5f5f5", text2: "#e5e5e5", text3: "#a8a29e",
};

// ─── ROLES ────────────────────────────────────────────────────────────────────
// IMPORTANT: the real backend only knows two roles — SuperAdmin and TenantAdmin
// (see API docs §3). Every /api/admin/* endpoint requires SuperAdmin; there is no
// PlatformAdmin / BillingAdmin / SupportAdmin / ReadOnlyAdmin on the server side.
// The finer-grained roles below are kept as a LOCAL, UI-ONLY permission simulation
// (no backend enforcement) so the front end still degrades gracefully if/when a
// real roles table is added later. Don't treat the "role switcher" in the topbar
// as real auth — it's a client-side view filter only.
const ROLES = ["SuperAdmin", "PlatformAdmin", "BillingAdmin", "SupportAdmin", "ReadOnlyAdmin"];

const PERMISSIONS = {
  SuperAdmin:    { tenants: "edit", plans: "edit", invoices: "edit", payments: "edit", audit: "view", dashboard: "view" },
  PlatformAdmin: { tenants: "edit", plans: "edit", invoices: "view", payments: "none", audit: "none", dashboard: "view" },
  BillingAdmin:  { tenants: "none", plans: "edit", invoices: "edit", payments: "edit", audit: "none", dashboard: "view" },
  SupportAdmin:  { tenants: "status-only", plans: "none", invoices: "none", payments: "none", audit: "none", dashboard: "view" },
  ReadOnlyAdmin: { tenants: "view", plans: "view", invoices: "view", payments: "view", audit: "view", dashboard: "view" },
};

function can(role, area, level = "edit") {
  const perm = PERMISSIONS[role]?.[area] || "none";
  if (level === "edit") return perm === "edit" || perm === "status-only";
  if (level === "view") return perm !== "none";
  return false;
}

const ROLE_NAV = {
  SuperAdmin:    ["dashboard", "tenants", "tenantDetail", "plans", "invoices", "payments", "audit", "users", "settings", "userDashboard"],
  PlatformAdmin: ["dashboard", "tenants", "tenantDetail", "plans", "invoices", "userDashboard"],
  BillingAdmin:  ["dashboard", "plans", "invoices", "payments", "userDashboard"],
  SupportAdmin:  ["dashboard", "tenants", "tenantDetail", "userDashboard"],
  ReadOnlyAdmin: ["dashboard", "tenants", "tenantDetail", "plans", "invoices", "payments", "audit", "users", "settings", "userDashboard"],
};
function pageAllowed(role, pageId) { return (ROLE_NAV[role] || []).includes(pageId); }

// ─── TOAST CONTEXT ────────────────────────────────────────────────────────────
const ToastContext = createContext(() => {});
function useToast() { return useContext(ToastContext); }

function ToastStack({ toasts }) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 w-[90vw] max-w-[380px]">
      {toasts.map(t => (
        <div key={t.id} 
          className="rounded-xl px-4 py-4 border shadow-2xl flex items-start gap-3 animate-[slideIn_0.3s_ease] backdrop-blur-sm"
          style={{ 
            background: t.type === "error" 
              ? "linear-gradient(135deg, rgba(239,68,68,0.1), rgba(185,28,28,0.1))"
              : "linear-gradient(135deg, rgba(52,211,153,0.1), rgba(16,185,129,0.1))",
            borderColor: t.type === "error" ? C.red : C.green,
            backdropFilter: "blur(12px)"
          }}>
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ background: t.type === "error" ? C.red : C.green }}>
              <i className={`ti ${t.type === "error" ? "ti-x" : "ti-check"} text-white text-xs`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold mb-0.5" 
              style={{ color: t.type === "error" ? C.red : C.green }}>
              {t.type === "error" ? "Error" : "Success"}
            </div>
            <div className="text-[13px] leading-relaxed" style={{ color: C.text1 }}>
              {t.message}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── GENERIC API-BACKED LIST HOOK ─────────────────────────────────────────────
// Wraps a call to one of the getX(filters) functions in superAdminApi.js.
// Handles: loading / error state, unwrapping the ApiResponse envelope, and both
// paged responses ({items, page, pageSize, totalCount, totalPages, ...}) and
// plain-array responses (e.g. GET /api/admin/plans, which is not paged).
// Call `reload()` after any create/update/delete mutation to refresh the list.
function useApiList(fetchFn, deps) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFn()
      .then(res => {
        if (cancelled) return;
        // NOTE: every getX() in superAdminApi.js already unwraps the ApiResponse
        // envelope (`return result.data || result`), so `res` here IS the payload —
        // do not read `res.data` again, or paged/array responses both come back empty.
        if (res && Array.isArray(res.items)) {
          setData(res.items);
          setMeta(res);
        } else {
          setData(Array.isArray(res) ? res : []);
          setMeta(null);
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || "Failed to load data.");
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  return { data, meta, loading, error, reload: () => setReloadToken(t => t + 1) };
}

// Normalizes an already-unwrapped API response (see superAdminApi.js — every
// function there already returns `result.data || result`) into a plain array,
// whether the endpoint is paged ({items, page, ...}) or returns a bare array
// (e.g. GET /api/admin/plans). Never call `.data` on these responses again —
// that was the bug causing empty tables/dropdowns even when the API returned data.
function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

// Debounce a fast-changing value (search inputs) so we don't fire a request per keystroke.
function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function Badge({ label, color = C.green, bg }) {
  const bgColor = bg || `${color}20`;
  return (
    <span className="text-[12px] font-semibold rounded-full px-2.5 py-0.5 flex-shrink-0"
      style={{ color, background: bgColor, border: `1px solid ${color}30` }}>
      {label}
    </span>
  );
}

function StatCard({ icon, label, value, delta, deltaUp, color = C.accent, sub }) {
  return (
    <div className="rounded-2xl border p-4 flex flex-col gap-2" style={{ background: C.card, borderColor: C.border }}>
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
          <i className={`ti ${icon} text-base`} style={{ color }} />
        </div>
        {delta && (
          <span className="text-[12px] font-semibold flex items-center gap-0.5" style={{ color: deltaUp ? C.green : C.red }}>
            <i className={`ti ${deltaUp ? "ti-trending-up" : "ti-trending-down"} text-xs`} />
            {delta}
          </span>
        )}
      </div>
      <div>
        <div className="text-2xl font-extrabold leading-tight" style={{ color: C.text1 }}>{value}</div>
        <div className="text-[12px] mt-0.5" style={{ color: C.text2 }}>{label}</div>
        {sub && <div className="text-[11px] mt-0.5" style={{ color: C.text3 }}>{sub}</div>}
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="rounded-2xl border p-4 flex flex-col gap-3" style={{ background: C.card, borderColor: C.border }}>
      <div className="w-9 h-9 rounded-xl animate-pulse" style={{ background: C.border2 }} />
      <div className="h-6 w-16 rounded animate-pulse" style={{ background: C.border2 }} />
      <div className="h-2.5 w-24 rounded animate-pulse" style={{ background: C.border2, opacity: 0.7 }} />
    </div>
  );
}

function SectionHeader({ title, sub, action, onAction, showAction = true }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center items-start justify-between gap-3 mb-4">
      <div>
        <h2 className="text-sm font-bold" style={{ color: C.text1 }}>{title}</h2>
        {sub && <p className="text-[12px] mt-0.5" style={{ color: C.text2 }}>{sub}</p>}
      </div>
      {action && showAction && (
        <button onClick={onAction}
          className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90 transition-opacity w-full sm:w-auto flex-shrink-0"
          style={{ background: C.accent }}>
          <i className="ti ti-plus text-xs" />
          {action}
        </button>
      )}
    </div>
  );
}

function Table({ cols, rows, onRow, emptyMessage = "No records found.", emptyCta }) {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border flex flex-col items-center justify-center gap-3 py-16" style={{ borderColor: C.border, background: C.card }}>
        <i className="ti ti-inbox text-3xl" style={{ color: C.text3 }} />
        <span className="text-[14px]" style={{ color: C.text3 }}>{emptyMessage}</span>
        {emptyCta && (
          <button onClick={emptyCta.onClick}
            className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90"
            style={{ background: C.accent }}>
            <i className="ti ti-plus text-xs" />
            {emptyCta.label}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <div className="rounded-2xl border overflow-hidden min-w-max lg:min-w-0" style={{ borderColor: C.border }}>
        <div className="grid text-[12px] uppercase tracking-wider px-4 py-2.5 border-b"
          style={{ gridTemplateColumns: cols.map(c => c.w || "1fr").join(" "), color: C.text3, borderColor: C.border, background: C.card2 }}>
          {cols.map(c => <span key={c.key}>{c.label}</span>)}
        </div>
        {rows.map((row, i) => (
          <div key={row.id ?? i} onClick={() => onRow && onRow(row)}
            className="grid items-center px-4 py-3 border-b transition-colors last:border-0"
            style={{ gridTemplateColumns: cols.map(c => c.w || "1fr").join(" "), borderColor: C.border, background: "transparent", cursor: onRow ? "pointer" : "default" }}
            onMouseEnter={e => { if (onRow) e.currentTarget.style.background = "#ffffff06"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            {cols.map(c => (
              <div key={c.key} className="min-w-0">
                {c.render ? c.render(row) : <span className="text-[14px] truncate block" style={{ color: C.text2 }}>{row[c.key]}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TableSkeleton({ rows = 5, cols = 6 }) {
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: C.border, background: C.card }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: C.border, background: C.card2 }}>
        <div className="h-2.5 rounded w-24" style={{ background: C.border2 }} />
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3.5 border-b last:border-0" style={{ borderColor: C.border }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="h-2.5 rounded flex-1 animate-pulse" style={{ background: C.border2, maxWidth: c === 0 ? 140 : 90, opacity: 0.5 + (r % 3) * 0.1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message = "Something went wrong loading this data.", onRetry }) {
  return (
    <div className="rounded-2xl border flex flex-col items-center justify-center gap-3 py-20" style={{ borderColor: `${C.red}30`, background: C.card }}>
      <i className="ti ti-alert-triangle text-3xl" style={{ color: C.red }} />
      <span className="text-[14px] text-center px-6" style={{ color: C.text2 }}>{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold border"
          style={{ background: C.card2, borderColor: C.border, color: C.text1 }}>
          <i className="ti ti-refresh text-xs" />
          Retry
        </button>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, hasPrevious, hasNext, onPage }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3 px-1">
      <span className="text-[12px]" style={{ color: C.text3 }}>Page {page} of {totalPages}</span>
      <div className="flex gap-2">
        <button disabled={!hasPrevious} onClick={() => onPage(page - 1)}
          className="text-[12px] rounded-lg px-2.5 py-1.5 border disabled:opacity-40" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Prev</button>
        <button disabled={!hasNext} onClick={() => onPage(page + 1)}
          className="text-[12px] rounded-lg px-2.5 py-1.5 border disabled:opacity-40" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Next</button>
      </div>
    </div>
  );
}

function MiniBar({ value, max, color = C.accent, unlimited = false }) {
  if (unlimited) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full" style={{ background: `${C.blue}30` }} />
        <span className="text-[12px] w-16 text-right" style={{ color: C.blue }}>Unlimited</span>
      </div>
    );
  }
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const col = pct >= 90 ? C.red : pct > 65 ? C.yellow : color;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full" style={{ background: C.border2 }}>
        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: col }} />
      </div>
      <span className="text-[12px] w-8 text-right" style={{ color: col }}>{pct}%</span>
    </div>
  );
}

function Toggle({ checked, onChange, color = C.accent, disabled = false }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      style={{ width: 36, height: 20, position: "relative", flexShrink: 0, opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: 999, background: checked ? color : C.border2, transition: "background .2s" }} />
      <span style={{ position: "absolute", top: 2, left: checked ? "calc(100% - 18px)" : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px #0006" }} />
    </button>
  );
}

function Input({ label, placeholder, value, onChange, type = "text", hint, disabled = false }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-[12px] uppercase tracking-wider font-medium" style={{ color: C.text3 }}>{label}</label>}
      {hint && <div className="text-[12px]" style={{ color: C.text3 }}>{hint}</div>}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border" style={{ background: disabled ? C.card : C.card2, borderColor: C.border2, opacity: disabled ? 0.6 : 1 }}>
        <input type={type} disabled={disabled}
          className="bg-transparent text-[14px] outline-none flex-1 placeholder-stone-600"
          style={{ color: C.text1 }} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, disabled = false }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-[12px] uppercase tracking-wider font-medium" style={{ color: C.text3 }}>{label}</label>}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border" style={{ background: disabled ? C.card : C.card2, borderColor: C.border2, opacity: disabled ? 0.6 : 1 }}>
        <select disabled={disabled} className="bg-transparent text-[14px] outline-none flex-1" style={{ color: C.text1 }}
          value={value} onChange={e => onChange(e.target.value)}>
          {options.map(o => (
            <option key={o.value ?? o} value={o.value ?? o} className="bg-stone-900">{o.label ?? o}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function TextArea({ label, placeholder, value, onChange, rows = 3 }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-[12px] uppercase tracking-wider font-medium" style={{ color: C.text3 }}>{label}</label>}
      <textarea rows={rows} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
        className="w-full rounded-xl px-3 py-2.5 border text-[14px] outline-none resize-none"
        style={{ background: C.card2, borderColor: C.border2, color: C.text1 }} />
    </div>
  );
}

function Divider() { return <div className="border-t" style={{ borderColor: C.border }} />; }

function Modal({ title, sub, onClose, children, width = 1000, footer }) {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-[fadeIn_0.2s_ease-out]" 
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }} 
      onClick={onClose}
    >
      <div 
        className="rounded-2xl border flex flex-col gap-4 p-6 max-h-[90vh] w-full max-w-[95vw] overflow-hidden shadow-2xl animate-[slideUp_0.3s_ease-out]"
        style={{ 
          background: "linear-gradient(135deg, #1f1e1c 0%, #1a1918 100%)", 
          borderColor: "#333",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.9), 0 0 0 1px rgba(232,96,42,0.1)",
          width 
        }} 
        onClick={e => e.stopPropagation()}
      >
        {/* Header with gradient accent */}
        <div className="flex items-start justify-between gap-3 pb-3 border-b" style={{ borderColor: C.border }}>
          <div className="min-w-0 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" 
              style={{ 
                background: "linear-gradient(135deg, #e8602a 0%, #c84a1e 100%)",
                boxShadow: "0 4px 12px rgba(232,96,42,0.3)"
              }}>
              <i className="ti ti-info-circle text-white text-lg" />
            </div>
            <div className="min-w-0 mt-1">
              <div className="text-lg font-extrabold" style={{ color: C.text1 }}>{title}</div>
              {sub && <div className="text-[13px] mt-1 opacity-70" style={{ color: C.text2 }}>{sub}</div>}
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
            style={{ 
              background: "rgba(255,255,255,0.05)", 
              color: C.text3,
              border: "1px solid rgba(255,255,255,0.1)"
            }}
          >
            <i className="ti ti-x text-base" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto pr-2 custom-scrollbar">
          {children}
        </div>
        
        {/* Footer */}
        {footer}
      </div>
      
      {/* Add custom scrollbar styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.03);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(232,96,42,0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(232,96,42,0.5);
        }
      `}</style>
    </div>
  );
}

function ConfirmDialog({ title, message, danger, confirmLabel = "Confirm", busy, onConfirm, onCancel }) {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-[fadeIn_0.2s_ease-out]" 
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }} 
      onClick={onCancel}
    >
      <div 
        className="rounded-2xl border flex flex-col gap-5 p-6 w-full max-w-[440px] shadow-2xl animate-[slideUp_0.3s_ease-out]"
        style={{ 
          background: "linear-gradient(135deg, #1f1e1c 0%, #1a1918 100%)", 
          borderColor: danger ? `${C.red}40` : "#333",
          boxShadow: danger 
            ? `0 25px 50px -12px rgba(239,68,68,0.4), 0 0 0 1px ${C.red}20`
            : "0 25px 50px -12px rgba(0,0,0,0.9), 0 0 0 1px rgba(232,96,42,0.1)"
        }} 
        onClick={e => e.stopPropagation()}
      >
        {/* Icon + Title */}
        <div className="flex items-start gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" 
            style={{ 
              background: danger 
                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                : "linear-gradient(135deg, #e8602a 0%, #c84a1e 100%)",
              boxShadow: danger
                ? "0 4px 12px rgba(239,68,68,0.4)"
                : "0 4px 12px rgba(232,96,42,0.3)"
            }}
          >
            <i className={`ti ${danger ? 'ti-alert-triangle' : 'ti-info-circle'} text-white text-xl`} />
          </div>
          <div className="flex-1 min-w-0 mt-1">
            <h3 className="text-lg font-extrabold mb-2" style={{ color: C.text1 }}>{title}</h3>
            <p className="text-[14px] leading-relaxed" style={{ color: C.text2, opacity: 0.85 }}>{message}</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <button 
            onClick={onCancel} 
            disabled={busy} 
            className="flex-1 py-3 rounded-xl text-sm font-semibold border transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              background: "rgba(255,255,255,0.05)", 
              borderColor: "rgba(255,255,255,0.1)", 
              color: C.text2 
            }}
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm} 
            disabled={busy}
            className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ 
              background: danger 
                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                : "linear-gradient(135deg, #e8602a 0%, #c84a1e 100%)",
              boxShadow: danger
                ? "0 4px 12px rgba(239,68,68,0.4)"
                : "0 4px 12px rgba(232,96,42,0.4)"
            }}
          >
            {busy ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Working…</span>
              </>
            ) : (
              <>
                <i className={`ti ${danger ? 'ti-alert-triangle' : 'ti-check'} text-base`} />
                <span>{confirmLabel}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function KebabMenu({ actions }) {
  const [open, setOpen] = useState(false);
  
  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(o => !o)}
        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-orange-500/30 transition-colors border"
        style={{ background: "rgba(255,255,255,0.1)", color: C.text1, borderColor: "rgba(255,255,255,0.2)" }}>
        <i className="ti ti-dots-vertical text-sm font-bold" />
      </button>
      
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div 
            className="fixed z-50 rounded-xl border py-1.5 min-w-[170px] shadow-2xl"
            style={{ 
              background: "#1a1815", 
              borderColor: "#333", 
              boxShadow: "0 10px 40px rgba(0,0,0,0.9)",
              transform: "translate(-140px, 8px)" // Position it to the left of the button
            }}>
            {actions.filter(a => a.show !== false).map((a, i) => (
              <button key={i} onClick={() => { setOpen(false); a.onClick(); }}
                className="w-full text-left px-4 py-3 text-[13px] hover:bg-orange-500/20 transition-colors flex items-center gap-3 border-b last:border-b-0"
                style={{ color: a.danger ? C.red : "#ffffff", borderColor: "rgba(255,255,255,0.1)" }}>
                {a.icon && <i className={`ti ${a.icon} text-sm`} />}
                <span className="font-medium">{a.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function money(amount, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount ?? 0);
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
}
function fmtDate(v) { if (!v) return "—"; try { return new Date(v).toLocaleDateString(); } catch { return v; } }
function fmtDateTime(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return v; } }

// ─── DISPLAY MAPS ─────────────────────────────────────────────────────────────
const planColor = { Starter: C.blue, Pro: C.accent, Enterprise: C.purple };
const tenantStatusColor = { Pending: C.yellow, Active: C.green, Suspended: C.red };
const subStatusColor = { Trialing: C.blue, Active: C.green, PastDue: C.red, Paused: C.yellow, Cancelled: C.text3, Expired: C.text3 };
const invoiceStatusColor = { Draft: C.text3, Pending: C.blue, Paid: C.green, Overdue: C.red, PartiallyPaid: C.yellow, Cancelled: C.text3, Refunded: C.purple };
const paymentStatusColor = { Pending: C.yellow, Processing: C.blue, Completed: C.green, Failed: C.red, Refunded: C.purple, Cancelled: C.text3 };

const auditActionLabels = {
  TenantCreated: "Tenant created", TenantStatusChanged: "Tenant status changed", TenantSuspended: "Tenant suspended",
  TenantActivated: "Tenant activated", PlanCreated: "Plan created", PlanUpdated: "Plan updated", PlanDeactivated: "Plan deactivated",
  SubscriptionAssigned: "Subscription assigned", SubscriptionChanged: "Subscription changed", SubscriptionCancelled: "Subscription cancelled",
  InvoiceCreated: "Invoice created", InvoiceIssued: "Invoice issued", InvoiceCancelled: "Invoice cancelled",
  PaymentRecorded: "Payment recorded", PaymentRefunded: "Payment refunded", ManualAdjustment: "Manual adjustment",
};

const lineItemTypes = ["Subscription", "Overage", "SetupFee", "Discount", "Tax", "Adjustment"];

function computeTotals(lineItems) {
  const subtotal = lineItems.filter(li => li.type !== "Tax" && li.type !== "Discount").reduce((s, li) => s + li.quantity * li.unitPrice, 0);
  const discount = Math.abs(lineItems.filter(li => li.type === "Discount").reduce((s, li) => s + li.quantity * li.unitPrice, 0));
  const tax = lineItems.filter(li => li.type === "Tax").reduce((s, li) => s + li.quantity * li.unitPrice, 0);
  const total = subtotal - discount + tax;
  return { subtotal, discount, tax, total };
}

// No backend endpoint exists for admin-team users/roles — kept as static preview
// data only (see explanation). If/when a real endpoint ships, swap this for a
// useApiList(() => getAdminUsers(), []) the same way every other page below does.
const adminUsers = [
  { id: "U1", name: "Ahmad (You)", email: "ahmad@awktechnologies.com", role: "SuperAdmin", status: "Active", lastLogin: "Now", mfa: true },
  { id: "U2", name: "Bilal Malik", email: "bilal@awktechnologies.com", role: "PlatformAdmin", status: "Active", lastLogin: "2h ago", mfa: true },
  { id: "U3", name: "Aisha Siddiqui", email: "aisha@awktechnologies.com", role: "SupportAdmin", status: "Active", lastLogin: "Yesterday", mfa: false },
  { id: "U4", name: "Zara Khan", email: "zara@awktechnologies.com", role: "BillingAdmin", status: "Inactive", lastLogin: "1w ago", mfa: false },
];

// ─── NAV ──────────────────────────────────────────────────────────────────────
const navSections = [
  { section: "PLATFORM", items: [
    { icon: "ti-layout-dashboard", label: "Dashboard", id: "dashboard" },
    { icon: "ti-building-store", label: "Tenants", id: "tenants" },
    { icon: "ti-package", label: "Subscription Plans", id: "plans" },
    { icon: "ti-receipt", label: "Invoices", id: "invoices" },
    { icon: "ti-credit-card", label: "Payments", id: "payments" },
    { icon: "ti-shield-check", label: "Audit Log", id: "audit" },
  ]},
  { section: "OPERATIONS", items: [
    { icon: "ti-users", label: "Admin Users", id: "users" },
    { icon: "ti-settings", label: "Settings", id: "settings" },
  ]},
  { section: "NAVIGATION", items: [
    { icon: "ti-arrow-back", label: "User Dashboard", id: "userDashboard", isExternal: true },
  ]},
];
function getVisibleNav(role) {
  return navSections
    .map(({ section, items }) => ({ section, items: items.filter(i => pageAllowed(role, i.id)) }))
    .filter(s => s.items.length > 0);
}

// ─── SIDEBAR / TOPBAR / MOBILE NAV ────────────────────────────────────────────
function Sidebar({ active, setActive, role }) {
  const visibleNav = getVisibleNav(role);
  return (
    <aside className="hidden lg:flex flex-col h-full border-r flex-shrink-0" style={{ width: 210, background: C.surface, borderColor: C.border }}>
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: C.border }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#e8602a,#c84a1e)" }}>
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-extrabold leading-tight" style={{ color: C.text1 }}>Nigella VOX</div>
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: C.accent }}>Super Admin</div>
        </div>
      </div>
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <style>{`
          nav::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {visibleNav.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[11px] uppercase tracking-widest mb-2 px-2" style={{ color: C.text3 }}>{section}</div>
            {items.map(({ icon, label, id }) => (
              <button key={id} onClick={() => setActive(id)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 w-full text-left transition-all"
                style={active === id ? { background: "#e8602a22", color: C.accent, border: "1px solid #e8602a35" } : { color: C.text2, border: "1px solid transparent" }}
                onMouseEnter={e => { if (active !== id) e.currentTarget.style.background = "#ffffff06"; }}
                onMouseLeave={e => { if (active !== id) e.currentTarget.style.background = "transparent"; }}>
                <i className={`ti ${icon} text-base flex-shrink-0`} />
                <span className="text-xs font-medium">{label}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: C.border }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full border-2 flex-shrink-0" style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)", borderColor: C.accent }} />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: C.text1 }}>{role}</div>
          </div>
        </div>
        <div className="text-[11px]" style={{ color: C.text3 }}>© 2026 AWK Technologies</div>
      </div>
    </aside>
  );
}

function MobileDrawer({ open, onClose, active, setActive, role }) {
  if (!open) return null;
  const visibleNav = getVisibleNav(role);
  return (
    <>
      <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose} />
      <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden" style={{ width: 260, background: C.surface, borderRight: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#e8602a,#c84a1e)" }}>
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-extrabold leading-tight" style={{ color: C.text1 }}>Nigella VOX</div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: C.accent }}>Super Admin</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: C.card2, color: C.text3 }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>
        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[11px] uppercase tracking-widest mb-2 px-2" style={{ color: C.text3 }}>{section}</div>
              {items.map(({ icon, label, id }) => (
                <button key={id} onClick={() => { setActive(id); onClose(); }}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 w-full text-left transition-all"
                  style={active === id ? { background: "#e8602a22", color: C.accent, border: "1px solid #e8602a35" } : { color: C.text2, border: "1px solid transparent" }}>
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full border-2 flex-shrink-0" style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)", borderColor: C.accent }} />
            <div>
              <div className="text-[13px] font-semibold" style={{ color: C.text1 }}>{role}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MobileBottomNav({ active, setActive, onMore, role }) {
  const allItems = [
    { icon: "ti-layout-dashboard", label: "Home", id: "dashboard" },
    { icon: "ti-building-store", label: "Tenants", id: "tenants" },
    { icon: "ti-receipt", label: "Invoices", id: "invoices" },
    { icon: "ti-credit-card", label: "Payments", id: "payments" },
  ];
  const items = allItems.filter(i => pageAllowed(role, i.id));
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t lg:hidden"
      style={{ background: C.surface, borderColor: C.border, paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map(({ icon, label, id }) => {
        const isActive = active === id;
        return (
          <button key={id} onClick={() => setActive(id)} className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: isActive ? C.accent : C.text3 }}>
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
      <button onClick={onMore} className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all" style={{ color: C.text3 }}>
        <i className="ti ti-dots text-lg" />
        <span style={{ fontSize: 11, fontWeight: 500 }}>More</span>
      </button>
    </nav>
  );
}

// Topbar owns its own live search — it calls the real search endpoints
// (getTenants / getInvoices / getPlans with a `search` filter) directly,
// debounced, instead of filtering an already-loaded local array.
function Topbar({ page, onMenuOpen, role, setRole, setActivePage, setSelectedTenantId }) {
  const titles = { dashboard: "Dashboard", tenants: "Tenants", tenantDetail: "Tenant Detail", plans: "Subscription Plans", invoices: "Invoices", payments: "Payments", audit: "Audit Log", users: "Admin Users", settings: "Settings" };
  const subs = { dashboard: "", tenants: "All tenant accounts", tenantDetail: "Account, subscription, usage & billing", plans: "Subscription plan management", invoices: "Tenant bills across the platform", payments: "All payments across tenants", audit: "Immutable trail of Super Admin actions", users: "Super admin team", settings: "Platform configuration" };
  const [roleOpen, setRoleOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 300);
  const [results, setResults] = useState({ tenants: [], invoices: [], plans: [] });
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!debouncedQuery) { setResults({ tenants: [], invoices: [], plans: [] }); return; }
    let cancelled = false;
    setSearching(true);
    Promise.allSettled([
      getTenants({ search: debouncedQuery, pageSize: 4 }),
      getInvoices({ search: debouncedQuery, pageSize: 4 }),
      getPlans({ search: debouncedQuery }),
    ]).then(([tRes, iRes, pRes]) => {
      if (cancelled) return;
      setResults({
        tenants: tRes.status === "fulfilled" ? toArray(tRes.value) : [],
        invoices: iRes.status === "fulfilled" ? toArray(iRes.value) : [],
        plans: pRes.status === "fulfilled" ? toArray(pRes.value).slice(0, 3) : [],
      });
      setSearching(false);
    });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const hasResults = results.tenants.length + results.invoices.length + results.plans.length > 0;

  function goToTenant(t) { setSelectedTenantId(t.id); setActivePage("tenantDetail"); setQuery(""); }
  function goToInvoice() { setActivePage("invoices"); setQuery(""); }
  function goToPlan() { setActivePage("plans"); setQuery(""); }

  return (
    <header className="flex items-center gap-3 lg:gap-4 px-4 lg:px-6 py-3 border-b flex-shrink-0" style={{ background: C.surface, borderColor: C.border }}>
      <button onClick={onMenuOpen} className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: C.card2, color: C.text2 }}>
        <i className="ti ti-menu-2 text-base" />
      </button>

      <div className="min-w-0">
        <div className="text-sm font-bold truncate" style={{ color: C.text1 }}>{titles[page]}</div>
        <div className="text-[12px] hidden sm:block" style={{ color: C.text3 }}>{subs[page]}</div>
      </div>

      <div className="hidden lg:block relative flex-1 mx-4">
        <div className="flex items-center gap-2 rounded-full px-4 py-2 border" style={{ background: C.bg, borderColor: C.border2 }}>
          <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search tenants, invoices, plans…"
            className="bg-transparent text-[13px] outline-none flex-1 placeholder-stone-600" style={{ color: C.text1 }} />
          {query && <button onClick={() => setQuery("")}><i className="ti ti-x text-xs" style={{ color: C.text3 }} /></button>}
        </div>
        {query && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setQuery("")} />
            <div className="absolute left-0 right-0 top-11 z-20 rounded-xl border shadow-xl overflow-hidden" style={{ background: C.card, borderColor: C.border }}>
              {searching && <div className="px-4 py-3 text-[13px]" style={{ color: C.text3 }}>Searching…</div>}
              {!searching && !hasResults && <div className="px-4 py-3 text-[13px]" style={{ color: C.text3 }}>No matches for "{debouncedQuery}".</div>}
              {results.tenants.length > 0 && (
                <div className="py-1">
                  <div className="px-4 py-1 text-[11px] uppercase tracking-wider" style={{ color: C.text3 }}>Tenants</div>
                  {results.tenants.map(t => (
                    <button key={t.id} onClick={() => goToTenant(t)} className="w-full text-left px-4 py-2 text-[13px] hover:opacity-80 flex items-center gap-2" style={{ color: C.text1 }}>
                      <i className="ti ti-building-store text-xs" style={{ color: C.accent }} /> {t.businessName}
                    </button>
                  ))}
                </div>
              )}
              {results.invoices.length > 0 && (
                <div className="py-1 border-t" style={{ borderColor: C.border }}>
                  <div className="px-4 py-1 text-[11px] uppercase tracking-wider" style={{ color: C.text3 }}>Invoices</div>
                  {results.invoices.map(i => (
                    <button key={i.id} onClick={goToInvoice} className="w-full text-left px-4 py-2 text-[13px] hover:opacity-80 flex items-center gap-2" style={{ color: C.text1 }}>
                      <i className="ti ti-receipt text-xs" style={{ color: C.blue }} /> {i.invoiceNumber} — {i.tenantName}
                    </button>
                  ))}
                </div>
              )}
              {results.plans.length > 0 && (
                <div className="py-1 border-t" style={{ borderColor: C.border }}>
                  <div className="px-4 py-1 text-[11px] uppercase tracking-wider" style={{ color: C.text3 }}>Plans</div>
                  {results.plans.map(p => (
                    <button key={p.id} onClick={goToPlan} className="w-full text-left px-4 py-2 text-[13px] hover:opacity-80 flex items-center gap-2" style={{ color: C.text1 }}>
                      <i className="ti ti-layers text-xs" style={{ color: C.purple }} /> {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <button onClick={() => setRoleOpen(o => !o)}
            className="hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1.5 border text-[12px] font-semibold"
            style={{ background: `${C.accent}15`, borderColor: `${C.accent}30`, color: C.accent }}>
            <i className="ti ti-shield-lock text-xs" />
            {role}
            <i className="ti ti-chevron-down text-xs" />
          </button>
          {roleOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setRoleOpen(false)} />
              <div className="absolute right-0 top-9 z-20 rounded-xl border py-1.5 min-w-[160px] shadow-xl" style={{ background: C.card, borderColor: C.border }}>
                {ROLES.map(r => (
                  <button key={r} onClick={() => { setRole(r); setRoleOpen(false); }}
                    className="w-full text-left px-3 py-2 text-[13px] hover:opacity-80"
                    style={{ color: r === role ? C.accent : C.text1 }}>
                    {r}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button className="w-8 h-8 rounded-full flex items-center justify-center relative flex-shrink-0" style={{ background: C.card2 }}>
          <i className="ti ti-bell text-sm" style={{ color: C.text2 }} />
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: C.accent }} />
        </button>

        {page !== "dashboard" && (
          <>
            <button onClick={() => setActivePage("dashboard")}
              className="hidden sm:flex items-center gap-2 rounded-full px-3 py-1.5 border hover:opacity-90 transition-opacity"
              style={{ background: C.card, borderColor: C.border }}>
              <div className="w-6 h-6 rounded-full border-2 flex-shrink-0" style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)", borderColor: C.accent }} />
              <div>
                <div className="text-[11px]" style={{ color: C.text3 }}>Back to Dashboard</div>
              </div>
            </button>

            <button onClick={() => setActivePage("dashboard")}
              className="sm:hidden w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2"
              style={{ background: "linear-gradient(135deg,#c84a1e,#e8602a)", borderColor: C.accent }} title="Back to Dashboard">
              <i className="ti ti-arrow-left text-white text-xs" />
            </button>
          </>
        )}
      </div>
    </header>
  );
}

// ─── CHART COMPONENTS ─────────────────────────────────────────────────────────

function MiniLineChart({ data, color = C.accent, height = 60 }) {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        fill={`url(#gradient-${color})`}
        points={`${points} 100,100 0,100`}
      />
    </svg>
  );
}

function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return null;
  
  let currentAngle = -90;
  const radius = 40;
  const centerX = 50;
  const centerY = 50;
  const strokeWidth = 12;
  
  return (
    <div style={{ width: size, height: size }} className="relative">
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={C.border2}
          strokeWidth={strokeWidth}
        />
        
        {/* Segments */}
        {segments.map((segment, i) => {
          const percentage = (segment.value / total) * 100;
          const angle = (percentage / 100) * 360;
          const endAngle = currentAngle + angle;
          
          const startRad = (currentAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          const x1 = centerX + radius * Math.cos(startRad);
          const y1 = centerY + radius * Math.sin(startRad);
          const x2 = centerX + radius * Math.cos(endRad);
          const y2 = centerY + radius * Math.sin(endRad);
          
          const largeArc = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`
          ].join(' ');
          
          currentAngle = endAngle;
          
          return (
            <path
              key={i}
              d={pathData}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-xl font-extrabold" style={{ color: C.text1 }}>{total}</div>
        <div className="text-[9px]" style={{ color: C.text3 }}>Total</div>
      </div>
    </div>
  );
}

function BarChart({ bars, height = 120 }) {
  const max = Math.max(...bars.map(b => b.value), 1);
  
  return (
    <div className="flex items-end justify-between gap-2" style={{ height }}>
      {bars.map((bar, i) => {
        const barHeight = (bar.value / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-2">
            <div className="text-[11px] font-bold" style={{ color: bar.color }}>{bar.value}</div>
            <div 
              className="w-full rounded-t-lg transition-all relative group cursor-pointer"
              style={{ 
                height: `${barHeight}%`,
                background: `linear-gradient(180deg, ${bar.color} 0%, ${bar.color}80 100%)`,
                minHeight: bar.value > 0 ? '4px' : '2px'
              }}
            >
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                {bar.label}: {bar.value}
              </div>
            </div>
            <div className="text-[9px] text-center" style={{ color: C.text3 }}>{bar.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DASHBOARD ── GET /api/admin/dashboard ────────────────────────────────────
function DashboardPage({ setActivePage, setSelectedTenantId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  function load() {
    setState(s => ({ ...s, loading: true, error: null }));
    getDashboardStats()
      .then(res => setState({ loading: false, error: null, data: res }))
      .catch(err => setState({ loading: false, error: err.message, data: null }));
  }
  useEffect(load, []);

  if (state.loading) {
    return (
      <div className="flex flex-col gap-5 p-4 lg:p-6 pb-24 lg:pb-6 overflow-y-auto flex-1">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)}</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)}</div>
      </div>
    );
  }
  if (state.error) return <div className="p-4 lg:p-6 flex-1"><ErrorState message={state.error} onRetry={load} /></div>;

  const d = state.data;
  
  // Mock data for charts (you can replace with real API data later)
  const revenueData = [2300, 2800, 3200, 2900, 3500, 4200, 3800];
  const tenantGrowthData = [5, 8, 12, 15, 18, 24, 28];
  
  const subscriptionSegments = [
    { label: 'Starter', value: d.activeTenants > 0 ? Math.floor(d.activeTenants * 0.4) : 2, color: C.blue },
    { label: 'Pro', value: d.activeTenants > 0 ? Math.floor(d.activeTenants * 0.45) : 3, color: C.accent },
    { label: 'Enterprise', value: d.activeTenants > 0 ? Math.ceil(d.activeTenants * 0.15) : 1, color: C.purple },
  ];
  
  const paymentBars = [
    { label: 'Paid', value: 45, color: C.green },
    { label: 'Pending', value: 12, color: C.yellow },
    { label: 'Overdue', value: d.overdueInvoiceCount || 2, color: C.red },
    { label: 'Failed', value: 3, color: C.text3 },
  ];

  return (
    <div className="flex flex-col gap-5 p-4 lg:p-6 pb-24 lg:pb-6 overflow-y-auto flex-1">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon="ti-building-store" label="Total Tenants" value={d.totalTenants} color={C.accent} />
        <StatCard icon="ti-circle-check" label="Active Tenants" value={d.activeTenants} color={C.green} />
        <StatCard icon="ti-clock" label="Pending Tenants" value={d.pendingTenants} color={C.yellow} />
        <StatCard icon="ti-ban" label="Suspended Tenants" value={d.suspendedTenants} color={C.text3} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon="ti-currency-dollar" label="Total Revenue (MTD)" value={money(d.revenueMtd)} color={C.green} />
        <StatCard icon="ti-alert-triangle" label="Overdue Invoices" value={d.overdueInvoiceCount} color={d.overdueInvoiceCount > 0 ? C.red : C.text3} />
        <StatCard icon="ti-repeat" label="Active Subscriptions" value={d.activeSubscriptions} color={C.blue} />
        <StatCard icon="ti-users" label="Total Leads (Platform)" value={(d.totalLeads ?? 0).toLocaleString()} color={C.purple} />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Trend Chart */}
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold" style={{ color: C.text1 }}>Revenue Trend</div>
              <div className="text-[11px] mt-0.5" style={{ color: C.text3 }}>Last 7 days</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-extrabold" style={{ color: C.green }}>{money(d.revenueMtd)}</div>
              <div className="text-[10px] flex items-center gap-1 justify-end" style={{ color: C.green }}>
                <i className="ti ti-trending-up text-xs" />
                <span>+12.5%</span>
              </div>
            </div>
          </div>
          <MiniLineChart data={revenueData} color={C.green} height={100} />
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: C.border }}>
            <div className="text-[11px]" style={{ color: C.text3 }}>Avg Daily: {money(d.revenueMtd / 30)}</div>
            <button onClick={() => setActivePage("payments")} className="text-[11px] font-semibold" style={{ color: C.accent }}>View Details →</button>
          </div>
        </div>

        {/* Tenant Growth Chart */}
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold" style={{ color: C.text1 }}>Tenant Growth</div>
              <div className="text-[11px] mt-0.5" style={{ color: C.text3 }}>Last 7 days</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-extrabold" style={{ color: C.accent }}>{d.totalTenants}</div>
              <div className="text-[10px] flex items-center gap-1 justify-end" style={{ color: C.green }}>
                <i className="ti ti-trending-up text-xs" />
                <span>+{d.pendingTenants} new</span>
              </div>
            </div>
          </div>
          <MiniLineChart data={tenantGrowthData} color={C.accent} height={100} />
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: C.border }}>
            <div className="text-[11px]" style={{ color: C.text3 }}>Active: {d.activeTenants} | Pending: {d.pendingTenants}</div>
            <button onClick={() => setActivePage("tenants")} className="text-[11px] font-semibold" style={{ color: C.accent }}>View Details →</button>
          </div>
        </div>
      </div>

      {/* Subscription Distribution & Payment Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Subscription Distribution Donut Chart */}
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold" style={{ color: C.text1 }}>Subscription Plans</div>
            <button onClick={() => setActivePage("plans")} className="text-[11px] font-semibold" style={{ color: C.accent }}>Manage →</button>
          </div>
          <div className="flex items-center justify-center py-4">
            <DonutChart segments={subscriptionSegments} size={140} />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {subscriptionSegments.map((seg, i) => (
              <div key={i} className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
                  <span className="text-[11px] font-semibold" style={{ color: C.text2 }}>{seg.value}</span>
                </div>
                <div className="text-[10px]" style={{ color: C.text3 }}>{seg.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Status Bar Chart */}
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold" style={{ color: C.text1 }}>Payment Status</div>
            <button onClick={() => setActivePage("invoices")} className="text-[11px] font-semibold" style={{ color: C.accent }}>View All →</button>
          </div>
          <BarChart bars={paymentBars} height={140} />
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t" style={{ borderColor: C.border }}>
            <div className="text-center">
              <div className="text-[11px]" style={{ color: C.text3 }}>Success Rate</div>
              <div className="text-lg font-extrabold" style={{ color: C.green }}>93%</div>
            </div>
            <div className="text-center">
              <div className="text-[11px]" style={{ color: C.text3 }}>Total Amount</div>
              <div className="text-lg font-extrabold" style={{ color: C.text1 }}>{money(d.revenueMtd)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Tenants & Overdue Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold" style={{ color: C.text1 }}>Recent Tenants</div>
            <button onClick={() => setActivePage("tenants")} className="text-[12px] font-semibold" style={{ color: C.accent }}>View all tenants →</button>
          </div>
          <div className="flex flex-col gap-3">
            {(d.recentTenants || []).map(t => (
              <div key={t.id} className="flex items-center gap-3">
                <button onClick={() => { setSelectedTenantId(t.id); setActivePage("tenantDetail"); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0"
                  style={{ background: planColor[t.planName] || C.accent }}>{t.businessName?.[0]}</button>
                <div className="flex-1 min-w-0">
                  <button onClick={() => { setSelectedTenantId(t.id); setActivePage("tenantDetail"); }} className="text-[14px] font-medium truncate block text-left hover:underline" style={{ color: C.text1 }}>{t.businessName}</button>
                  <div className="text-[11px] truncate" style={{ color: C.text3 }}>{t.contactEmail}</div>
                </div>
                <Badge label={t.status} color={tenantStatusColor[t.status]} />
                <span className="text-[12px] w-14 text-right" style={{ color: C.text3 }}>{t.planName}</span>
              </div>
            ))}
            {(!d.recentTenants || d.recentTenants.length === 0) && <div className="text-[13px] py-4 text-center" style={{ color: C.text3 }}>No tenants yet.</div>}
          </div>
        </div>

        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-bold" style={{ color: C.text1 }}>Overdue Invoices</div>
            <button onClick={() => setActivePage("invoices")} className="text-[12px] font-semibold" style={{ color: C.accent }}>View all invoices →</button>
          </div>
          {(!d.overdueInvoices || d.overdueInvoices.length === 0) ? (
            <div className="text-[13px] py-6 text-center" style={{ color: C.text3 }}>No overdue invoices — nice work.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {d.overdueInvoices.slice(0, 5).map(inv => (
                <div key={inv.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${C.red}18` }}>
                    <i className="ti ti-alert-circle text-xs" style={{ color: C.red }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium truncate" style={{ color: C.text1 }}>{inv.tenantName}</div>
                    <div className="text-[11px] font-mono" style={{ color: C.text3 }}>{inv.invoiceNumber}</div>
                  </div>
                  <span className="text-[13px] font-bold" style={{ color: C.red }}>{money(inv.outstandingAmount ?? inv.totalAmount, inv.currency)}</span>
                  <span className="text-[11px] w-16 text-right" style={{ color: C.red }}>{inv.daysOverdue}d overdue</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TENANTS LIST ── GET /api/admin/tenants, PUT .../status ───────────────────
function TenantsPage({ setActivePage, setSelectedTenantId, role }) {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterOnboarded, setFilterOnboarded] = useState("All");
  const [page, setPage] = useState(1);
  const [confirmAction, setConfirmAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canEdit = can(role, "tenants");

  useEffect(() => { setPage(1); }, [search, filterStatus, filterOnboarded]);

  const { data: tenants, meta, loading, error, reload } = useApiList(
    () => getTenants({
      search: search || undefined,
      status: filterStatus === "All" ? undefined : filterStatus,
      onboarded: filterOnboarded === "All" ? undefined : filterOnboarded === "Yes",
      page, pageSize: 20,
    }),
    [search, filterStatus, filterOnboarded, page]
  );

  async function setStatus(tenantId, newStatus) {
    setBusy(true);
    try {
      await updateTenantStatus(tenantId, newStatus);
      toast(`Tenant status updated to ${newStatus} successfully!`);
      reload();
    } catch (e) {
      toast(`Failed to update tenant status: ${e.message}`, "error");
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-w-0 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
        <SectionHeader title="Tenants" sub={meta ? `${meta.totalCount} total accounts` : ""} />

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background: C.card, borderColor: C.border }}>
            <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
            <input className="bg-transparent text-[14px] outline-none w-32 sm:w-44 placeholder-stone-600" style={{ color: C.text1 }}
              placeholder="Search by business or email…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
          </div>

          {["All", "Pending", "Active", "Suspended"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className="text-[12px] font-medium rounded-full px-3 py-1.5 border transition-all flex-shrink-0"
              style={filterStatus === s
                ? { background: `${tenantStatusColor[s] || C.accent}20`, color: tenantStatusColor[s] || C.accent, borderColor: `${tenantStatusColor[s] || C.accent}40` }
                : { background: C.card, color: C.text3, borderColor: "transparent" }}>
              {s}
            </button>
          ))}

          <select value={filterOnboarded} onChange={e => setFilterOnboarded(e.target.value)}
            className="text-[12px] rounded-xl px-3 py-2 border bg-transparent lg:ml-auto" style={{ borderColor: C.border, color: C.text2, background: C.card }}>
            <option className="bg-stone-900" value="All">Onboarded: All</option>
            <option className="bg-stone-900" value="Yes">Onboarded: Yes</option>
            <option className="bg-stone-900" value="No">Onboarded: No</option>
          </select>
        </div>

        {loading && <TableSkeleton cols={8} />}
        {!loading && error && <ErrorState message={error} onRetry={reload} />}
        {!loading && !error && (
          <>
            <Table
              emptyMessage="No tenants registered yet."
              cols={[
                { key: "businessName", label: "Business", w: "1.6fr", render: r => (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0" style={{ background: planColor[r.planName] || C.accent }}>{r.businessName?.[0]}</div>
                    <div className="min-w-0">
                      <button onClick={() => { setSelectedTenantId(r.id); setActivePage("tenantDetail"); }} className="text-[14px] font-semibold truncate block text-left hover:underline" style={{ color: C.text1 }}>{r.businessName}</button>
                      <div className="text-[12px] truncate" style={{ color: C.text3 }}>{r.contactEmail}</div>
                    </div>
                  </div>
                )},
                { key: "status", label: "Status", w: "90px", render: r => <Badge label={r.status} color={tenantStatusColor[r.status]} /> },
                { key: "planName", label: "Plan", w: "90px" },
                { key: "subscriptionStatus", label: "Subscription", w: "100px", render: r => <Badge label={r.subscriptionStatus} color={subStatusColor[r.subscriptionStatus]} /> },
                { key: "isOnboarded", label: "Onboarded", w: "80px", render: r => <i className={`ti ${r.isOnboarded ? "ti-check" : "ti-minus"}`} style={{ color: r.isOnboarded ? C.green : C.text3 }} /> },
                { key: "knowledgeEntryCount", label: "KB", w: "50px", render: r => <span className="text-[14px]" style={{ color: C.text2 }}>{r.knowledgeEntryCount ?? 0}</span> },
                { key: "conversationCount", label: "Convos", w: "70px", render: r => <span className="text-[14px]" style={{ color: C.text2 }}>{(r.conversationCount ?? 0).toLocaleString()}</span> },
                { key: "leadCount", label: "Leads", w: "70px", render: r => <span className="text-[14px]" style={{ color: C.text2 }}>{(r.leadCount ?? 0).toLocaleString()}</span> },
                { key: "ecosystem", label: "Ecosystem", w: "110px", render: r => (
                  <div className="flex gap-1">
                    {r.isNigellaFitUser && <Badge label="Fit" color={C.blue} />}
                    {r.isNigellaErpUser && <Badge label="ERP" color={C.purple} />}
                    {!r.isNigellaFitUser && !r.isNigellaErpUser && <span className="text-[12px]" style={{ color: C.text3 }}>—</span>}
                  </div>
                )},
                { key: "createdAtUtc", label: "Joined", w: "90px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{fmtDate(r.createdAtUtc)}</span> },
                { key: "actions", label: "", w: "50px", render: r => (
                  <KebabMenu actions={[
                    { label: "View details", icon: "ti-eye", onClick: () => { setSelectedTenantId(r.id); setActivePage("tenantDetail"); } },
                    { label: "Set Active", icon: "ti-circle-check", show: canEdit && r.status !== "Active", onClick: () => setConfirmAction({ tenantId: r.id, name: r.businessName, next: "Active" }) },
                    { label: "Set Pending", icon: "ti-clock", show: canEdit && r.status !== "Pending", onClick: () => setConfirmAction({ tenantId: r.id, name: r.businessName, next: "Pending" }) },
                    { label: "Set Suspended", icon: "ti-ban", danger: true, show: canEdit && r.status !== "Suspended", onClick: () => setConfirmAction({ tenantId: r.id, name: r.businessName, next: "Suspended" }) },
                  ]} />
                )},
              ]}
              rows={tenants}
            />
            {meta && <Pagination page={meta.page} totalPages={meta.totalPages} hasPrevious={meta.hasPrevious} hasNext={meta.hasNext} onPage={setPage} />}
          </>
        )}
      </div>

      {confirmAction && (
        <ConfirmDialog
          title={`Set status to ${confirmAction.next}`}
          message={`This will change ${confirmAction.name}'s account status to "${confirmAction.next}". This action is logged to the audit trail.`}
          danger={confirmAction.next === "Suspended"}
          confirmLabel={`Set ${confirmAction.next}`}
          busy={busy}
          onConfirm={() => setStatus(confirmAction.tenantId, confirmAction.next)}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ─── TENANT DETAIL ── GET .../{id}, usage, subscription assign/cancel ─────────
function TenantDetailPage({ tenantId, role, setActivePage }) {
  const [tab, setTab] = useState("overview");
  const [tenantState, setTenantState] = useState({ loading: true, error: null, data: null });
  const [usageState, setUsageState] = useState({ loading: false, error: null, data: null });
  const [tenantInvoices, setTenantInvoices] = useState({ loading: false, error: null, data: [] });
  const [showAssignPlan, setShowAssignPlan] = useState(false);
  const [showCancelSub, setShowCancelSub] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canEdit = can(role, "tenants");

  function loadTenant() {
    if (!tenantId) return;
    setTenantState(s => ({ ...s, loading: true, error: null }));
    getTenantById(tenantId)
      .then(res => setTenantState({ loading: false, error: null, data: res }))
      .catch(err => setTenantState({ loading: false, error: err.message, data: null }));
  }
  useEffect(loadTenant, [tenantId]);

  useEffect(() => {
    if (!tenantId || tab !== "usage") return;
    setUsageState(s => ({ ...s, loading: true, error: null }));
    getTenantUsage(tenantId)
      .then(res => setUsageState({ loading: false, error: null, data: res }))
      .catch(err => setUsageState({ loading: false, error: err.message, data: null }));
  }, [tenantId, tab]);

  useEffect(() => {
    if (!tenantId || tab !== "invoices") return;
    setTenantInvoices(s => ({ ...s, loading: true, error: null }));
    getInvoices({ tenantId, pageSize: 50 })
      .then(res => setTenantInvoices({ loading: false, error: null, data: toArray(res) }))
      .catch(err => setTenantInvoices({ loading: false, error: err.message, data: [] }));
  }, [tenantId, tab]);

  if (tenantState.loading) {
    return <div className="p-4 lg:p-6 flex-1"><TableSkeleton rows={6} cols={2} /></div>;
  }
  if (tenantState.error) {
    return <div className="p-4 lg:p-6 flex-1"><ErrorState message={tenantState.error} onRetry={loadTenant} /></div>;
  }
  const tenant = tenantState.data;
  if (!tenant) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <i className="ti ti-mood-empty text-3xl mb-2" style={{ color: C.text3 }} />
          <div className="text-[14px]" style={{ color: C.text2 }}>Tenant not found.</div>
          <button onClick={() => setActivePage("tenants")} className="text-[13px] font-semibold mt-2" style={{ color: C.accent }}>← Back to Tenants</button>
        </div>
      </div>
    );
  }
  const sub = tenant.currentSubscription;

  async function setStatus(newStatus) {
    setBusy(true);
    try {
      await updateTenantStatus(tenantId, newStatus);
      toast(`Tenant status set to ${newStatus}.`);
      loadTenant();
    } catch (e) { toast(`Failed to update tenant status: ${e.message}`, "error"); }
    finally { setBusy(false); setConfirmStatus(null); }
  }

  async function assignPlan(subscriptionPlanId, autoRenew, startTrial, notes) {
    setBusy(true);
    try {
      await assignSubscription(tenantId, { subscriptionPlanId, startTrial, autoRenew, notes });
      toast("Subscription plan assigned successfully!");
      loadTenant();
      setShowAssignPlan(false);
    } catch (e) { toast(`Failed to assign subscription: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  async function cancelSub(reason, endImmediately) {
    setBusy(true);
    try {
      await cancelSubscription(tenantId, { cancellationReason: reason, endImmediately });
      toast(endImmediately ? "Subscription cancelled immediately!" : "Subscription cancelled — access continues until period end!");
      loadTenant();
      setShowCancelSub(false);
    } catch (e) { toast(`Failed to cancel subscription: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "subscription", label: "Subscription" },
    { id: "usage", label: "Usage" },
    { id: "invoices", label: "Invoices" },
    { id: "audit", label: "Audit" },
  ];

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <button onClick={() => setActivePage("tenants")} className="text-[13px] font-medium mb-3 flex items-center gap-1 w-fit" style={{ color: C.text3 }}>
        <i className="ti ti-arrow-left text-xs" /> Back to Tenants
      </button>

      <div className="rounded-2xl border p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" style={{ background: C.card, borderColor: C.border }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white flex-shrink-0" style={{ background: planColor[tenant.planName] || C.accent }}>
            {tenant.businessName?.[0]}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold" style={{ color: C.text1 }}>{tenant.businessName}</h2>
              <Badge label={tenant.status} color={tenantStatusColor[tenant.status]} />
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <select value={tenant.status} onChange={e => setConfirmStatus(e.target.value)}
              className="text-[13px] rounded-xl px-3 py-2 border bg-transparent" style={{ borderColor: C.border, color: C.text2, background: C.card2 }}>
              {["Pending", "Active", "Suspended"].map(s => <option key={s} className="bg-stone-900">{s}</option>)}
            </select>
            <button onClick={() => setShowAssignPlan(true)} className="text-[13px] font-semibold rounded-xl px-3 py-2 border" style={{ background: `${C.accent}15`, borderColor: `${C.accent}40`, color: C.accent }}>
              Assign / Change Plan
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-5 border-b overflow-x-auto" style={{ borderColor: C.border }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="text-[13px] font-semibold px-4 py-2.5 border-b-2 flex-shrink-0 transition-colors"
            style={tab === t.id ? { color: C.accent, borderColor: C.accent } : { color: C.text3, borderColor: "transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon="ti-message-dots" label="Conversations" value={(tenant.conversationCount ?? 0).toLocaleString()} color={C.blue} />
            <StatCard icon="ti-users" label="Leads" value={(tenant.leadCount ?? 0).toLocaleString()} color={C.purple} />
            <StatCard icon="ti-book" label="KB Entries" value={tenant.knowledgeEntryCount ?? 0} color={C.green} />
            <StatCard icon="ti-plug" label="Channels" value={tenant.channelCount ?? 0} color={C.accent} />
          </div>
          <div className="rounded-2xl border p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ background: C.card, borderColor: C.border }}>
            {[
              ["Business name", tenant.businessName], ["Contact email", tenant.contactEmail],
              ["Account status", tenant.status], ["Onboarded", tenant.isOnboarded ? "Yes" : "No"],
              ["Nigella Fit", tenant.isNigellaFitUser ? "Enabled" : "—"], ["Nigella ERP", tenant.isNigellaErpUser ? "Enabled" : "—"],
              ["Registered", fmtDate(tenant.createdAtUtc)], ["Last updated", fmtDate(tenant.updatedAtUtc)],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between rounded-xl px-3 py-2.5 border" style={{ background: C.card2, borderColor: C.border2 }}>
                <span className="text-[12px]" style={{ color: C.text3 }}>{label}</span>
                <span className="text-[13px] font-medium" style={{ color: C.text1 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "subscription" && (
        <div className="flex flex-col gap-5">
          {!sub && (
            <div className="rounded-2xl border p-8 text-center" style={{ background: C.card, borderColor: C.border }}>
              <div className="text-[14px] mb-3" style={{ color: C.text2 }}>This tenant has no active subscription.</div>
              {canEdit && <button onClick={() => setShowAssignPlan(true)} className="text-[13px] font-semibold rounded-xl px-3 py-2 border" style={{ background: `${C.accent}15`, borderColor: `${C.accent}40`, color: C.accent }}>Assign a plan</button>}
            </div>
          )}
          {sub && (
            <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm font-bold" style={{ color: C.text1 }}>{sub.planName} <span className="text-[12px] font-mono ml-1" style={{ color: C.text3 }}>{sub.planCode}</span></div>
                  <div className="text-[13px]" style={{ color: C.text2 }}>{money(sub.price, sub.currency)} / {(sub.billingCycle || "").toLowerCase().replace("ly", "")}</div>
                </div>
                <Badge label={sub.status} color={subStatusColor[sub.status]} />
              </div>
              <Divider />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {[
                  ["Started", fmtDate(sub.startedAtUtc)], ["Trial ends", fmtDate(sub.trialEndsAtUtc)],
                  ["Current period start", fmtDate(sub.currentPeriodStartUtc)], ["Current period end", fmtDate(sub.currentPeriodEndUtc)],
                  ["Auto-renew", sub.autoRenew ? "Yes" : "No"],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between rounded-xl px-3 py-2.5 border" style={{ background: C.card2, borderColor: C.border2 }}>
                    <span className="text-[12px]" style={{ color: C.text3 }}>{label}</span>
                    <span className="text-[13px] font-medium" style={{ color: C.text1 }}>{value}</span>
                  </div>
                ))}
              </div>
              {canEdit && sub.status !== "Cancelled" && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setShowAssignPlan(true)} className="text-[13px] font-semibold rounded-xl px-3 py-2 border" style={{ background: `${C.accent}15`, borderColor: `${C.accent}40`, color: C.accent }}>Change plan</button>
                  <button onClick={() => setShowCancelSub(true)} className="text-[13px] font-semibold rounded-xl px-3 py-2 border" style={{ background: `${C.red}15`, borderColor: `${C.red}40`, color: C.red }}>Cancel subscription</button>
                </div>
              )}
            </div>
          )}

          {tenant.subscriptionHistory?.length > 0 && (
            <div>
              <div className="text-[12px] uppercase tracking-wider font-bold mb-2" style={{ color: C.text3 }}>Previous subscriptions</div>
              <div className="flex flex-col gap-2">
                {tenant.subscriptionHistory.map((h, i) => (
                  <div key={i} className="flex justify-between rounded-xl px-3 py-2.5 border text-[13px]" style={{ background: C.card2, borderColor: C.border2, color: C.text2 }}>
                    <span>{h.planName}</span><Badge label={h.status} color={subStatusColor[h.status] || C.text3} /><span>{fmtDate(h.startedAtUtc)} → {fmtDate(h.endsAtUtc)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "usage" && (
        <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.border }}>
          <div className="text-sm font-bold mb-4" style={{ color: C.text1 }}>Current billing period usage</div>
          {usageState.loading && <TableSkeleton rows={4} cols={2} />}
          {!usageState.loading && usageState.error && <ErrorState message={usageState.error} />}
          {!usageState.loading && !usageState.error && usageState.data && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(usageState.data.metrics || []).map(u => (
                <div key={u.metricType} className="rounded-xl border p-3" style={{ background: C.card2, borderColor: C.border2 }}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-[13px] font-medium" style={{ color: C.text1 }}>{u.metricType}</span>
                    <span className="text-[12px]" style={{ color: C.text3 }}>{u.usedQuantity?.toLocaleString()}{u.limitQuantity > 0 ? ` / ${u.limitQuantity.toLocaleString()}` : ""}</span>
                  </div>
                  <MiniBar value={u.usedQuantity} max={u.limitQuantity} unlimited={!u.limitQuantity || u.limitQuantity === 0} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "invoices" && (
        <div className="flex flex-col gap-3">
          {tenantInvoices.loading && <TableSkeleton cols={5} />}
          {!tenantInvoices.loading && tenantInvoices.error && <ErrorState message={tenantInvoices.error} />}
          {!tenantInvoices.loading && !tenantInvoices.error && (
            <Table
              emptyMessage="No invoices for this tenant yet."
              cols={[
                { key: "invoiceNumber", label: "Invoice #", w: "1.4fr", render: r => <span className="font-mono text-[13px]" style={{ color: C.text1 }}>{r.invoiceNumber}</span> },
                { key: "status", label: "Status", w: "100px", render: r => <Badge label={r.status} color={invoiceStatusColor[r.status]} /> },
                { key: "issueDateUtc", label: "Issued", w: "100px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{fmtDate(r.issueDateUtc)}</span> },
                { key: "dueDateUtc", label: "Due", w: "100px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{fmtDate(r.dueDateUtc)}</span> },
                { key: "totalAmount", label: "Total", w: "100px", render: r => <span className="font-bold text-[14px]" style={{ color: C.accent }}>{money(r.totalAmount, r.currency)}</span> },
              ]}
              rows={tenantInvoices.data}
            />
          )}
        </div>
      )}

      {tab === "audit" && (
        <div className="rounded-2xl border p-5 text-center" style={{ background: C.card, borderColor: C.border }}>
          <div className="text-[13px]" style={{ color: C.text2 }}>Tenant-scoped audit entries appear in the platform-wide <button className="underline" style={{ color: C.accent }} onClick={() => setActivePage("audit")}>Audit Log</button>, filtered to {tenant.businessName}.</div>
        </div>
      )}

      {showAssignPlan && <AssignPlanModal onClose={() => setShowAssignPlan(false)} onAssign={assignPlan} busy={busy} />}
      {showCancelSub && <CancelSubscriptionModal onClose={() => setShowCancelSub(false)} onCancel={cancelSub} busy={busy} />}

      {confirmStatus && (
        <ConfirmDialog
          title={`Set status to ${confirmStatus}`}
          message={`This will change ${tenant.businessName}'s account status to "${confirmStatus}". This action is logged to the audit trail.`}
          danger={confirmStatus === "Suspended"}
          confirmLabel={`Set ${confirmStatus}`}
          busy={busy}
          onConfirm={() => setStatus(confirmStatus)}
          onCancel={() => setConfirmStatus(null)}
        />
      )}
    </div>
  );
}

// Plan picker here loads real plans (GET /api/admin/plans) — the backend expects
// a subscriptionPlanId (GUID), not a plan name, so the Select stores plan.id.
function AssignPlanModal({ onClose, onAssign, busy }) {
  const [plansState, setPlansState] = useState({ loading: true, error: null, data: [] });
  const [planId, setPlanId] = useState("");
  const [autoRenew, setAutoRenew] = useState(true);
  const [startTrial, setStartTrial] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    getPlans({ isActive: true })
      .then(res => {
        const list = toArray(res);
        setPlansState({ loading: false, error: null, data: list });
        if (list.length) setPlanId(list[0].id);
      })
      .catch(err => setPlansState({ loading: false, error: err.message, data: [] }));
  }, []);

  const selectedPlan = plansState.data.find(p => p.id === planId);

  return (
    <Modal title="Assign / Change Plan" sub="Update this tenant's subscription plan" onClose={onClose}>
      {plansState.loading && <div className="text-[13px] py-4 text-center" style={{ color: C.text3 }}>Loading plans…</div>}
      {plansState.error && <ErrorState message={plansState.error} />}
      {!plansState.loading && !plansState.error && (
        <div className="flex flex-col gap-3">
          <Select label="Plan" value={planId} onChange={setPlanId} options={plansState.data.map(p => ({ value: p.id, label: `${p.name} — ${money(p.price, p.currency)}/${p.billingCycle.toLowerCase()}` }))} />
          {selectedPlan?.trialDays > 0 && (
            <div className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: C.card2, borderColor: C.border2 }}>
              <Toggle checked={startTrial} onChange={setStartTrial} />
              <div>
                <div className="text-[14px] font-medium" style={{ color: C.text1 }}>Start trial</div>
                <div className="text-[12px]" style={{ color: C.text3 }}>{selectedPlan.trialDays}-day trial available on this plan</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: C.card2, borderColor: C.border2 }}>
            <Toggle checked={autoRenew} onChange={setAutoRenew} />
            <div>
              <div className="text-[14px] font-medium" style={{ color: C.text1 }}>Auto-renew</div>
              <div className="text-[12px]" style={{ color: C.text3 }}>Automatically renew at period end</div>
            </div>
          </div>
          <TextArea label="Notes (internal)" placeholder="Optional context for this change…" value={notes} onChange={setNotes} />
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => onAssign(planId, autoRenew, startTrial, notes)} disabled={!planId || busy}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.accent }}>
          {busy ? "Assigning…" : "Assign plan"}
        </button>
        <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Cancel</button>
      </div>
    </Modal>
  );
}

function CancelSubscriptionModal({ onClose, onCancel, busy }) {
  const [reason, setReason] = useState("");
  const [endImmediately, setEndImmediately] = useState(false);
  return (
    <Modal title="Cancel Subscription" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <TextArea label="Cancellation reason *" placeholder="Why is this subscription being cancelled?" value={reason} onChange={setReason} />
        <div className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: C.card2, borderColor: C.border2 }}>
          <Toggle checked={endImmediately} onChange={setEndImmediately} color={C.red} />
          <div>
            <div className="text-[14px] font-medium" style={{ color: C.text1 }}>End immediately</div>
            <div className="text-[12px]" style={{ color: C.text3 }}>Otherwise access continues until the current period ends</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button disabled={!reason.trim() || busy} onClick={() => onCancel(reason, endImmediately)}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.red }}>
          {busy ? "Cancelling…" : "Cancel subscription"}
        </button>
        <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Back</button>
      </div>
    </Modal>
  );
}

// ─── PLANS ── GET/POST/PUT/DELETE /api/admin/plans ────────────────────────────
const emptyPlanForm = {
  code: "", name: "", description: "", sortOrder: 0,
  billingCycle: "Monthly", price: 0, currency: "USD", trialDays: 0,
  maxConversationsPerMonth: 0, maxMessagesPerMonth: 0, maxLeadsPerMonth: 0,
  maxKnowledgeBaseEntries: 0, maxChannels: 0, maxUsers: 0, maxAiTokensPerMonth: 0,
  isActive: true, isPublic: true,
};

function PlanForm({ initial, isEdit, onSave, onCancel, onDeactivate, busy }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const limitFields = [
    ["maxConversationsPerMonth", "Max conversations / month"], ["maxMessagesPerMonth", "Max messages / month"],
    ["maxLeadsPerMonth", "Max leads / month"], ["maxKnowledgeBaseEntries", "Max KB entries"],
    ["maxChannels", "Max channels"], ["maxUsers", "Max users"], ["maxAiTokensPerMonth", "Max AI tokens / month"],
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="text-[12px] uppercase tracking-wider font-bold mb-3" style={{ color: C.text1 }}>A · Basic Info</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Code" placeholder="pro" value={form.code} onChange={v => set("code", v.toLowerCase().replace(/\s+/g, "-"))} disabled={isEdit} />
          <Input label="Name" placeholder="Pro Plan" value={form.name} onChange={v => set("name", v)} />
        </div>
        <div className="mt-3"><TextArea label="Description" placeholder="Shown on pricing page" value={form.description} onChange={v => set("description", v)} /></div>
        <div className="mt-3 sm:w-1/3"><Input label="Sort order" type="number" value={form.sortOrder} onChange={v => set("sortOrder", Number(v))} /></div>
      </div>
      <Divider />
      <div>
        <div className="text-[12px] uppercase tracking-wider font-bold mb-3" style={{ color: C.text1 }}>B · Pricing</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Select label="Billing cycle" value={form.billingCycle} onChange={v => set("billingCycle", v)} options={["Monthly", "Quarterly", "Yearly"]} />
          <Input label="Price" type="number" value={form.price} onChange={v => set("price", Number(v))} />
          <Select label="Currency" value={form.currency} onChange={v => set("currency", v)} options={["USD"]} />
          <Input label="Trial days" type="number" hint="0 = no trial" value={form.trialDays} onChange={v => set("trialDays", Number(v))} />
        </div>
      </div>
      <Divider />
      <div>
        <div className="text-[12px] uppercase tracking-wider font-bold mb-3" style={{ color: C.text1 }}>C · Plan Limits</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {limitFields.map(([key, label]) => (
            <Input key={key} label={label} type="number" hint="0 = unlimited" value={form[key]} onChange={v => set(key, Number(v))} />
          ))}
        </div>
      </div>
      <Divider />
      <div>
        <div className="text-[12px] uppercase tracking-wider font-bold mb-3" style={{ color: C.text1 }}>D · Visibility</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-xl px-4 py-3 border" style={{ background: C.card2, borderColor: C.border2 }}>
            <div><div className="text-[14px] font-medium" style={{ color: C.text1 }}>Is active</div><div className="text-[12px]" style={{ color: C.text3 }}>Available for assignment</div></div>
            <Toggle checked={form.isActive} onChange={v => set("isActive", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl px-4 py-3 border" style={{ background: C.card2, borderColor: C.border2 }}>
            <div><div className="text-[14px] font-medium" style={{ color: C.text1 }}>Is public</div><div className="text-[12px]" style={{ color: C.text3 }}>Visible on signup / pricing page</div></div>
            <Toggle checked={form.isPublic} onChange={v => set("isPublic", v)} />
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => onSave(form)} disabled={!form.code || !form.name || busy}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.accent }}>
          {busy ? "Saving…" : "Save plan"}
        </button>
        <button onClick={onCancel} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Cancel</button>
        {isEdit && onDeactivate && (
          <button onClick={onDeactivate} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: `${C.red}15`, borderColor: `${C.red}30`, color: C.red }}>Deactivate plan</button>
        )}
      </div>
    </div>
  );
}

function PlansPage({ role }) {
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canEdit = can(role, "plans");

  const { data: plans, loading, error, reload } = useApiList(
    () => getPlans({ search: search || undefined, isActive: filterStatus === "All" ? undefined : filterStatus === "Active" }),
    [search, filterStatus]
  );

  async function savePlan(form) {
    setBusy(true);
    try {
      if (editing) { 
        await updatePlan(editing.id, form); 
        toast(`"${form.name}" plan updated successfully!`); 
      }
      else { 
        await createPlan(form); 
        toast(`New plan "${form.name}" created successfully!`); 
      }
      setEditing(null); setShowAdd(false);
      reload();
    } catch (e) { 
      toast(`Failed to ${editing ? 'update' : 'create'} plan: ${e.message}`, "error"); 
    }
    finally { setBusy(false); }
  }

  async function deactivatePlan(plan) {
    setBusy(true);
    try {
      await deletePlan(plan.id);
      toast(`"${plan.name}" plan deactivated successfully!`);
      setConfirmDeactivate(null); setEditing(null);
      reload();
    } catch (e) { toast(`Failed to deactivate plan: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <SectionHeader title="Subscription Plans" sub="Plan catalog management" action="New Plan" onAction={() => setShowAdd(true)} showAction={canEdit} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background: C.card, borderColor: C.border }}>
          <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
          <input className="bg-transparent text-[14px] outline-none w-40 placeholder-stone-600" style={{ color: C.text1 }}
            placeholder="Search name or code…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        </div>
        {["All", "Active", "Inactive"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className="text-[12px] font-medium rounded-full px-3 py-1.5 border transition-all"
            style={filterStatus === s ? { background: `${C.accent}20`, color: C.accent, borderColor: `${C.accent}40` } : { background: C.card, color: C.text3, borderColor: "transparent" }}>
            {s}
          </button>
        ))}
      </div>

      {loading && <TableSkeleton cols={7} />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && (
        <Table
          emptyMessage="No subscription plans yet. Create your first plan."
          cols={[
            { key: "name", label: "Name", w: "1.4fr", render: p => (
              <div>
                <button onClick={() => canEdit && setEditing(p)} className="text-[14px] font-semibold text-left hover:underline" style={{ color: C.text1 }}>{p.name}</button>
                <div className="text-[12px] font-mono" style={{ color: C.text3 }}>{p.code}</div>
              </div>
            )},
            { key: "price", label: "Price", w: "90px", render: p => <span className="font-bold text-[14px]" style={{ color: C.accent }}>{money(p.price, p.currency)}</span> },
            { key: "billingCycle", label: "Cycle", w: "90px", render: p => <span className="text-[14px]" style={{ color: C.text2 }}>{p.billingCycle}</span> },
            { key: "trialDays", label: "Trial", w: "70px", render: p => <span className="text-[14px]" style={{ color: C.text2 }}>{p.trialDays > 0 ? `${p.trialDays}d` : "—"}</span> },
            { key: "isActive", label: "Active", w: "70px", render: p => <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.isActive ? C.green : C.text3 }} /> },
            { key: "isPublic", label: "Public", w: "70px", render: p => <span className="text-[14px]" style={{ color: C.text2 }}>{p.isPublic ? "Yes" : "No"}</span> },
            { key: "subscriberCount", label: "Subscribers", w: "90px", render: p => <span className="text-[14px]" style={{ color: C.text2 }}>{p.subscriberCount}</span> },
            { key: "actions", label: "", w: "100px", render: p => canEdit && (
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="text-[12px] rounded-lg px-2.5 py-1.5 border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Edit</button>
                {p.isActive && <button onClick={() => setConfirmDeactivate(p)} className="text-[12px] rounded-lg px-2.5 py-1.5 border" style={{ background: `${C.red}12`, borderColor: `${C.red}30`, color: C.red }}>Deactivate</button>}
              </div>
            )},
          ]}
          rows={plans}
        />
      )}

      {editing && (
        <Modal title={`Edit ${editing.name} Plan`} sub="Update pricing, limits and features" onClose={() => setEditing(null)} width={1200}>
          <PlanForm initial={editing} isEdit onSave={savePlan} onCancel={() => setEditing(null)} onDeactivate={() => setConfirmDeactivate(editing)} busy={busy} />
        </Modal>
      )}
      {showAdd && (
        <Modal title="Create New Plan" sub="Add a new subscription tier" onClose={() => setShowAdd(false)} width={1200}>
          <PlanForm initial={emptyPlanForm} isEdit={false} onSave={savePlan} onCancel={() => setShowAdd(false)} busy={busy} />
        </Modal>
      )}
      {confirmDeactivate && (
        <ConfirmDialog title="Deactivate plan" message={`${confirmDeactivate.name} will no longer be assignable to new tenants. Existing subscribers are unaffected.`}
          danger confirmLabel="Deactivate" busy={busy} onConfirm={() => deactivatePlan(confirmDeactivate)} onCancel={() => setConfirmDeactivate(null)} />
      )}
    </div>
  );
}

// ─── INVOICES ── list/create/issue/cancel/pay ─────────────────────────────────
function LineItemsEditor({ lineItems, setLineItems }) {
  function update(id, key, value) { setLineItems(prev => prev.map(li => li.id === id ? { ...li, [key]: value } : li)); }
  function remove(id) { setLineItems(prev => prev.filter(li => li.id !== id)); }
  function add() { setLineItems(prev => [...prev, { id: `LI${Date.now()}`, type: "Subscription", description: "", quantity: 1, unitPrice: 0, sortOrder: prev.length + 1 }]); }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[100px_1.5fr_60px_90px_90px_28px] gap-2 text-[11px] uppercase tracking-wider px-1" style={{ color: C.text3 }}>
        <span>Type</span><span>Description</span><span>Qty</span><span>Unit price</span><span>Amount</span><span />
      </div>
      {lineItems.map(li => (
        <div key={li.id} className="grid grid-cols-[100px_1.5fr_60px_90px_90px_28px] gap-2 items-center">
          <select value={li.type} onChange={e => update(li.id, "type", e.target.value)}
            className="text-[12px] rounded-lg px-2 py-2 border bg-transparent" style={{ borderColor: C.border2, color: C.text1, background: C.card2 }}>
            {lineItemTypes.map(t => <option key={t} className="bg-stone-900">{t}</option>)}
          </select>
          <input value={li.description} onChange={e => update(li.id, "description", e.target.value)} placeholder="Description"
            className="text-[13px] rounded-lg px-2 py-2 border bg-transparent outline-none" style={{ borderColor: C.border2, color: C.text1, background: C.card2 }} />
          <input type="number" value={li.quantity} onChange={e => update(li.id, "quantity", Number(e.target.value))}
            className="text-[13px] rounded-lg px-2 py-2 border bg-transparent outline-none" style={{ borderColor: C.border2, color: C.text1, background: C.card2 }} />
          <input type="number" value={li.unitPrice} onChange={e => update(li.id, "unitPrice", Number(e.target.value))}
            className="text-[13px] rounded-lg px-2 py-2 border bg-transparent outline-none" style={{ borderColor: C.border2, color: C.text1, background: C.card2 }} />
          <span className="text-[13px] font-semibold px-2" style={{ color: C.text1 }}>{money(li.quantity * li.unitPrice)}</span>
          <button onClick={() => remove(li.id)} className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: C.card2, color: C.red }}>
            <i className="ti ti-trash text-xs" />
          </button>
        </div>
      ))}
      <button onClick={add} className="text-[12px] font-semibold rounded-lg px-3 py-2 border w-fit mt-1" style={{ background: C.card2, borderColor: C.border2, color: C.accent }}>+ Add line item</button>
    </div>
  );
}

// Tenants are fetched here (GET /api/admin/tenants) so the invoice can be tied
// to a real tenantId (GUID) — the backend has no concept of "tenant name" as a key.
function InvoiceForm({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const [tenantsState, setTenantsState] = useState({ loading: true, error: null, data: [] });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLineItems = updater => setForm(f => ({ ...f, lineItems: typeof updater === "function" ? updater(f.lineItems) : updater }));
  const totals = computeTotals(form.lineItems);

  useEffect(() => {
    getTenants({ pageSize: 100 })
      .then(res => {
        const items = toArray(res);
        setTenantsState({ loading: false, error: null, data: items });
        if (items.length && !form.tenantId) set("tenantId", items[0].id);
      })
      .catch(err => setTenantsState({ loading: false, error: err.message, data: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {tenantsState.error && <ErrorState message={tenantsState.error} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select label="Tenant" value={form.tenantId} onChange={v => set("tenantId", v)}
          options={tenantsState.data.map(t => ({ value: t.id, label: t.businessName }))} disabled={tenantsState.loading} />
        <Select label="Currency" value={form.currency} onChange={v => set("currency", v)} options={["USD"]} />
        <Input label="Due date" type="date" value={form.dueDateUtc} onChange={v => set("dueDateUtc", v)} />
        <Input label="Billing period start" type="date" value={form.billingPeriodStartUtc || ""} onChange={v => set("billingPeriodStartUtc", v)} />
        <Input label="Billing period end" type="date" value={form.billingPeriodEndUtc || ""} onChange={v => set("billingPeriodEndUtc", v)} />
      </div>
      <TextArea label="Notes (tenant-visible)" value={form.notes} onChange={v => set("notes", v)} />

      <Divider />
      <div className="text-[12px] uppercase tracking-wider font-bold" style={{ color: C.accent }}>Line Items</div>
      <LineItemsEditor lineItems={form.lineItems} setLineItems={setLineItems} />

      <div className="rounded-xl border p-4 flex flex-col gap-1.5 self-end w-full sm:w-64" style={{ background: C.card2, borderColor: C.border2 }}>
        <div className="flex justify-between text-[13px]"><span style={{ color: C.text3 }}>Subtotal</span><span style={{ color: C.text1 }}>{money(totals.subtotal, form.currency)}</span></div>
        <div className="flex justify-between text-[13px]"><span style={{ color: C.text3 }}>Discount</span><span style={{ color: C.red }}>−{money(totals.discount, form.currency)}</span></div>
        <div className="flex justify-between text-[13px]"><span style={{ color: C.text3 }}>Tax</span><span style={{ color: C.text1 }}>{money(totals.tax, form.currency)}</span></div>
        <Divider />
        <div className="flex justify-between text-[15px] font-bold"><span style={{ color: C.text1 }}>Total</span><span style={{ color: C.accent }}>{money(totals.total, form.currency)}</span></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => onSave(form)} disabled={!form.tenantId || busy}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.accent }}>
          {busy ? "Saving…" : "Save invoice"}
        </button>
        <button onClick={onCancel} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Cancel</button>
      </div>
    </div>
  );
}

function RecordPaymentModal({ invoice, onClose, onRecord, busy }) {
  const outstanding = invoice.outstandingAmount ?? (invoice.totalAmount - (invoice.paidAmount || 0));
  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState("Credit Card");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [externalId, setExternalId] = useState("");
  const [externalProvider, setExternalProvider] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <Modal title="Record Payment" sub={`Invoice ${invoice.invoiceNumber} · ${invoice.tenantName}`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between rounded-xl px-3 py-2.5 border" style={{ background: C.card2, borderColor: C.border2 }}>
          <span className="text-[12px]" style={{ color: C.text3 }}>Outstanding balance</span>
          <span className="text-[14px] font-bold" style={{ color: C.accent }}>{money(outstanding, invoice.currency)}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Amount" type="number" value={amount} onChange={v => setAmount(Number(v))} />
          <Select label="Payment method" value={method} onChange={setMethod} options={["Credit Card", "Bank Transfer", "PayPal", "Manual", "Other"]} />
          <Input label="Payment date" type="date" value={date} onChange={setDate} />
          <Input label="External payment ID" placeholder="ch_1P8x…" value={externalId} onChange={setExternalId} />
          <Input label="External provider" placeholder="Stripe" value={externalProvider} onChange={setExternalProvider} />
          <Input label="Receipt URL" placeholder="https://…" value={receiptUrl} onChange={setReceiptUrl} />
        </div>
        <TextArea label="Notes" value={notes} onChange={setNotes} />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => onRecord({ amount, method: PAYMENT_METHOD_TO_API[method] || method, date, externalId, externalProvider, receiptUrl, notes })}
          disabled={amount <= 0 || amount > outstanding || busy} className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.accent }}>
          {busy ? "Recording…" : "Record payment"}
        </button>
        <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 rounded-xl text-xs font-medium border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>Cancel</button>
      </div>
    </Modal>
  );
}

function InvoicesPage({ role, setActivePage }) {
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState({ loading: false, error: null, data: null });
  const [showCreate, setShowCreate] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canEdit = can(role, "invoices");

  useEffect(() => { setPage(1); }, [search, filterStatus]);

  const { data: invoices, meta, loading, error, reload } = useApiList(
    () => getInvoices({ search: search || undefined, status: filterStatus === "All" ? undefined : filterStatus, page, pageSize: 20 }),
    [search, filterStatus, page]
  );

  // Full detail (line items, payments) isn't in the list response — fetch it
  // when a row is opened.
  useEffect(() => {
    if (!selected) { setSelectedDetail({ loading: false, error: null, data: null }); return; }
    setSelectedDetail({ loading: true, error: null, data: null });
    getInvoiceById(selected.id)
      .then(res => setSelectedDetail({ loading: false, error: null, data: res }))
      .catch(err => setSelectedDetail({ loading: false, error: err.message, data: null }));
  }, [selected]);

  const emptyInvoiceForm = {
    tenantId: "", currency: "USD", dueDateUtc: "", billingPeriodStartUtc: "", billingPeriodEndUtc: "", notes: "",
    lineItems: [{ id: "LI1", type: "Subscription", description: "", quantity: 1, unitPrice: 0, sortOrder: 1 }],
  };

  async function handleCreate(form) {
    setBusy(true);
    try {
      await createInvoice({
        tenantId: form.tenantId, dueDateUtc: form.dueDateUtc, billingPeriodStartUtc: form.billingPeriodStartUtc || undefined,
        billingPeriodEndUtc: form.billingPeriodEndUtc || undefined, currency: form.currency, notes: form.notes,
        lineItems: form.lineItems.map(({ id, ...li }) => li),
      });
      toast("Draft invoice created successfully!");
      setShowCreate(false);
      reload();
    } catch (e) { toast(`Failed to create invoice: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  async function handleIssue(inv) {
    setBusy(true);
    try { await issueInvoice(inv.id); toast(`Invoice ${inv.invoiceNumber} issued successfully!`); reload(); refreshSelected(inv.id); }
    catch (e) { toast(`Failed to issue invoice: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  async function handleCancel(inv) {
    setBusy(true);
    try { await cancelInvoice(inv.id); toast(`Invoice ${inv.invoiceNumber} cancelled successfully!`); setConfirmCancel(null); reload(); refreshSelected(inv.id); }
    catch (e) { toast(`Failed to cancel invoice: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  async function handleRecordPayment(inv, payload) {
    setBusy(true);
    try {
      await recordPayment(inv.id, { amount: payload.amount, method: payload.method, paidAtUtc: payload.date, externalPaymentId: payload.externalId, externalProvider: payload.externalProvider, receiptUrl: payload.receiptUrl, notes: payload.notes });
      toast(`Payment of ${money(payload.amount, inv.currency)} recorded successfully!`);
      setShowRecordPayment(null);
      reload();
      refreshSelected(inv.id);
    } catch (e) { toast(`Failed to record payment: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  function refreshSelected(id) {
    if (selected?.id !== id) return;
    getInvoiceById(id).then(res => setSelectedDetail({ loading: false, error: null, data: res })).catch(() => {});
  }

  const detail = selectedDetail.data || selected;

  const DetailContent = selected && (
    <>
      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: C.border }}>
        <span className="text-xs font-semibold" style={{ color: C.text1 }}>Invoice Detail</span>
        <button onClick={() => setSelected(null)} className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: C.card2, color: C.text3 }}>
          <i className="ti ti-x text-xs" />
        </button>
      </div>
      <div className="flex flex-col gap-4 p-5">
        {selectedDetail.loading && <TableSkeleton rows={5} cols={2} />}
        {selectedDetail.error && <ErrorState message={selectedDetail.error} />}
        {detail && (
          <>
            <div>
              <div className="text-[13px] font-mono mb-2" style={{ color: C.text3 }}>{detail.invoiceNumber}</div>
              <div className="text-lg font-bold" style={{ color: C.text1 }}>{money(detail.totalAmount, detail.currency)}</div>
              <Badge label={detail.status} color={invoiceStatusColor[detail.status]} />
            </div>
            <Divider />
            {[["Tenant", detail.tenantName], ["Issue date", fmtDate(detail.issueDateUtc)], ["Due date", fmtDate(detail.dueDateUtc)],
              ["Subtotal", money(detail.subtotalAmount, detail.currency)], ["Discount", money(detail.discountAmount, detail.currency)],
              ["Tax", money(detail.taxAmount, detail.currency)], ["Outstanding", money(detail.outstandingAmount ?? (detail.totalAmount - (detail.paidAmount || 0)), detail.currency)]].map(([label, value]) => (
              <div key={label} className="flex justify-between rounded-xl px-3 py-2.5 border" style={{ background: C.card, borderColor: C.border }}>
                <span className="text-[12px]" style={{ color: C.text3 }}>{label}</span>
                <span className="text-[13px] font-medium" style={{ color: C.text1 }}>{value}</span>
              </div>
            ))}

            {detail.lineItems?.length > 0 && (
              <div>
                <div className="text-[12px] uppercase tracking-wider font-bold mb-2" style={{ color: C.text3 }}>Line items</div>
                {detail.lineItems.map((li, i) => (
                  <div key={li.id || i} className="flex justify-between text-[13px] py-1.5" style={{ color: C.text2 }}>
                    <span>{li.description || li.type}</span>
                    <span className="font-semibold" style={{ color: C.text1 }}>{money(li.quantity * li.unitPrice, detail.currency)}</span>
                  </div>
                ))}
              </div>
            )}

            {detail.payments?.length > 0 && (
              <div>
                <div className="text-[12px] uppercase tracking-wider font-bold mb-2" style={{ color: C.text3 }}>Payments</div>
                {detail.payments.map(p => (
                  <div key={p.id} className="flex justify-between text-[13px] py-1.5" style={{ color: C.text2 }}>
                    <span>{fmtDate(p.paidAtUtc)} · {PAYMENT_METHOD_FROM_API[p.method] || p.method}</span>
                    <span className="font-semibold" style={{ color: C.green }}>{money(p.amount, p.currency)}</span>
                  </div>
                ))}
              </div>
            )}

            <Divider />
            <div className="flex flex-col gap-2">
              {canEdit && detail.status === "Draft" && (
                <button onClick={() => handleIssue(detail)} disabled={busy} className="w-full py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.accent }}>Issue invoice</button>
              )}
              {canEdit && ["Pending", "Overdue", "PartiallyPaid"].includes(detail.status) && (
                <button onClick={() => setShowRecordPayment(detail)} disabled={busy} className="w-full py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.green }}>Record payment</button>
              )}
              {canEdit && ["Draft", "Pending"].includes(detail.status) && (
                <button onClick={() => setConfirmCancel(detail)} disabled={busy} className="w-full py-2.5 rounded-xl text-xs font-medium border disabled:opacity-50" style={{ background: C.card, borderColor: `${C.red}30`, color: C.red }}>Cancel invoice</button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-w-0 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
        <SectionHeader title="Invoices" sub="All tenant bills across the platform" action="Create Invoice" onAction={() => setShowCreate(true)} showAction={canEdit} />

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background: C.card, borderColor: C.border }}>
            <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
            <input className="bg-transparent text-[14px] outline-none w-40 placeholder-stone-600" style={{ color: C.text1 }}
              placeholder="Invoice # or tenant…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
          </div>
          {["All", "Draft", "Pending", "Paid", "Overdue", "PartiallyPaid", "Cancelled", "Refunded"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className="text-[12px] font-medium rounded-full px-3 py-1.5 border transition-all flex-shrink-0"
              style={filterStatus === s ? { background: `${invoiceStatusColor[s] || C.accent}20`, color: invoiceStatusColor[s] || C.accent, borderColor: `${invoiceStatusColor[s] || C.accent}40` } : { background: C.card, color: C.text3, borderColor: "transparent" }}>
              {s}
            </button>
          ))}
        </div>

        {loading && <TableSkeleton cols={6} />}
        {!loading && error && <ErrorState message={error} onRetry={reload} />}
        {!loading && !error && (
          <>
            <Table
              cols={[
                { key: "invoiceNumber", label: "Invoice #", w: "1.3fr", render: r => <span className="font-mono text-[13px]" style={{ color: C.text1 }}>{r.invoiceNumber}</span> },
                { key: "tenantName", label: "Tenant", w: "1.2fr" },
                { key: "status", label: "Status", w: "100px", render: r => <Badge label={r.status} color={invoiceStatusColor[r.status]} /> },
                { key: "issueDateUtc", label: "Issued", w: "95px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{fmtDate(r.issueDateUtc)}</span> },
                { key: "dueDateUtc", label: "Due", w: "95px", render: r => <span style={{ color: r.status === "Overdue" ? C.red : C.text2 }}>{fmtDate(r.dueDateUtc)}</span> },
                { key: "totalAmount", label: "Total", w: "100px", render: r => <span className="font-bold text-[14px]" style={{ color: C.accent }}>{money(r.totalAmount, r.currency)}</span> },
                { key: "actions", label: "Actions", w: "160px", render: r => (
                  <div className="flex gap-1">
                    <button onClick={() => setSelected(r)} className="text-[12px] rounded-lg px-2.5 py-1.5 border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>View</button>
                    {canEdit && ["Pending", "Overdue", "PartiallyPaid"].includes(r.status) && (
                      <button onClick={() => setShowRecordPayment(r)} className="text-[12px] rounded-lg px-2.5 py-1.5 border" style={{ background: `${C.green}12`, borderColor: `${C.green}30`, color: C.green }}>Pay</button>
                    )}
                  </div>
                )},
              ]}
              rows={invoices}
            />
            {meta && <Pagination page={meta.page} totalPages={meta.totalPages} hasPrevious={meta.hasPrevious} hasNext={meta.hasNext} onPage={setPage} />}
          </>
        )}
      </div>

      {selected && (
        <div className="hidden lg:flex flex-col border-l flex-shrink-0 overflow-y-auto" style={{ width: 320, background: C.surface, borderColor: C.border }}>
          {DetailContent}
        </div>
      )}
      {selected && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.65)" }} onClick={() => setSelected(null)} />
          <div className="relative flex flex-col h-full w-[88%] max-w-[360px] overflow-y-auto" style={{ background: C.surface }}>{DetailContent}</div>
        </div>
      )}

      {showCreate && (
        <Modal title="Create Invoice" sub="Issue a new bill to a tenant" onClose={() => setShowCreate(false)} width={1200}>
          <InvoiceForm initial={emptyInvoiceForm} onSave={handleCreate} onCancel={() => setShowCreate(false)} busy={busy} />
        </Modal>
      )}
      {showRecordPayment && (
        <RecordPaymentModal invoice={showRecordPayment} onClose={() => setShowRecordPayment(null)} onRecord={payload => handleRecordPayment(showRecordPayment, payload)} busy={busy} />
      )}
      {confirmCancel && (
        <ConfirmDialog title="Cancel invoice" message={`This will cancel invoice ${confirmCancel.invoiceNumber}. This cannot be undone.`} danger confirmLabel="Cancel invoice" busy={busy}
          onConfirm={() => handleCancel(confirmCancel)} onCancel={() => setConfirmCancel(null)} />
      )}
    </div>
  );
}

// ─── PAYMENTS ── GET /api/admin/payments (read-only) ──────────────────────────
function PaymentsPage({ setActivePage }) {
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterMethod, setFilterMethod] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [search, filterStatus, filterMethod]);

  const { data: payments, meta, loading, error, reload } = useApiList(
    () => getPayments({
      search: search || undefined,
      status: filterStatus === "All" ? undefined : filterStatus,
      method: filterMethod === "All" ? undefined : PAYMENT_METHOD_TO_API[filterMethod],
      page, pageSize: 20,
    }),
    [search, filterStatus, filterMethod, page]
  );

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <SectionHeader title="Payments" sub="All payments across tenants" showAction={false} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background: C.card, borderColor: C.border }}>
          <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
          <input className="bg-transparent text-[14px] outline-none w-40 placeholder-stone-600" style={{ color: C.text1 }}
            placeholder="Payment ID, invoice #, tenant…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        </div>
        {["All", "Pending", "Processing", "Completed", "Failed", "Refunded", "Cancelled"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className="text-[12px] font-medium rounded-full px-3 py-1.5 border transition-all flex-shrink-0"
            style={filterStatus === s ? { background: `${paymentStatusColor[s] || C.accent}20`, color: paymentStatusColor[s] || C.accent, borderColor: `${paymentStatusColor[s] || C.accent}40` } : { background: C.card, color: C.text3, borderColor: "transparent" }}>
            {s}
          </button>
        ))}
        <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}
          className="text-[12px] rounded-xl px-3 py-2 border bg-transparent lg:ml-auto" style={{ borderColor: C.border, color: C.text2, background: C.card }}>
          <option className="bg-stone-900">All</option>
          {["Credit Card", "Bank Transfer", "PayPal", "Manual", "Other"].map(m => <option key={m} className="bg-stone-900">{m}</option>)}
        </select>
      </div>

      {loading && <TableSkeleton cols={8} />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && (
        <>
          <Table
            emptyMessage="No payments recorded yet."
            cols={[
              { key: "paidAtUtc", label: "Date", w: "100px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{fmtDate(r.paidAtUtc)}</span> },
              { key: "tenantName", label: "Tenant", w: "1.2fr" },
              { key: "invoiceNumber", label: "Invoice #", w: "1.2fr", render: r => <span className="font-mono text-[13px]" style={{ color: C.text1 }}>{r.invoiceNumber}</span> },
              { key: "amount", label: "Amount", w: "100px", render: r => <span className="font-bold text-[14px]" style={{ color: C.green }}>{money(r.amount, r.currency)}</span> },
              { key: "method", label: "Method", w: "110px", render: r => <span className="text-[14px]" style={{ color: C.text1 }}>{PAYMENT_METHOD_FROM_API[r.method] || r.method}</span> },
              { key: "status", label: "Status", w: "100px", render: r => <Badge label={r.status} color={paymentStatusColor[r.status]} /> },
              { key: "externalProvider", label: "Provider", w: "90px", render: r => r.externalProvider || "—" },
              { key: "externalPaymentId", label: "Reference", w: "110px", render: r => <span className="font-mono text-[12px]" style={{ color: C.text3 }}>{r.externalPaymentId || "—"}</span> },
              { key: "actions", label: "", w: "80px", render: () => (
                <button onClick={() => setActivePage("invoices")} className="text-[12px] rounded-lg px-2.5 py-1.5 border" style={{ background: C.card2, borderColor: C.border, color: C.text2 }}>View invoice</button>
              )},
            ]}
            rows={payments}
          />
          {meta && <Pagination page={meta.page} totalPages={meta.totalPages} hasPrevious={meta.hasPrevious} hasNext={meta.hasNext} onPage={setPage} />}
        </>
      )}
    </div>
  );
}

// ─── AUDIT LOG ── GET /api/admin/audit-log (read-only) ────────────────────────
// NOTE: the real endpoint does NOT return a before/after `detailsJson` diff —
// only actorEmail, action, entityType/entityId, targetTenantName, summary,
// ipAddress and createdAtUtc. The expandable JSON diff from the mock version
// has been dropped since the backend has nothing to show there.
function AuditLogPage() {
  const [filterAction, setFilterAction] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { setPage(1); }, [search, filterAction]);

  const { data: entries, meta, loading, error, reload } = useApiList(
    () => getAuditLogs({ search: search || undefined, action: filterAction === "All" ? undefined : filterAction, page, pageSize: 50 }),
    [search, filterAction, page]
  );

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <SectionHeader title="Audit Log" sub="Immutable trail of all Super Admin actions · read-only" showAction={false} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ background: C.card, borderColor: C.border }}>
          <i className="ti ti-search text-xs" style={{ color: C.text3 }} />
          <input className="bg-transparent text-[14px] outline-none w-44 placeholder-stone-600" style={{ color: C.text1 }}
            placeholder="Search summary or tenant…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        </div>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
          className="text-[12px] rounded-xl px-3 py-2 border bg-transparent" style={{ borderColor: C.border, color: C.text2, background: C.card }}>
          <option className="bg-stone-900" value="All">All</option>
          {Object.keys(auditActionLabels).map(a => <option key={a} value={a} className="bg-stone-900">{auditActionLabels[a]}</option>)}
        </select>
      </div>

      {loading && <TableSkeleton rows={8} cols={5} />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && (
        <>
          <div className="flex flex-col gap-2">
            {entries.length === 0 && (
              <div className="rounded-2xl border flex flex-col items-center justify-center gap-2 py-16" style={{ borderColor: C.border, background: C.card }}>
                <i className="ti ti-inbox text-3xl" style={{ color: C.text3 }} />
                <span className="text-[14px]" style={{ color: C.text3 }}>No audit entries match these filters.</span>
              </div>
            )}
            {entries.map(a => (
              <div key={a.id} className="rounded-2xl border overflow-hidden" style={{ borderColor: C.border, background: C.card }}>
                <button onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="w-full flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 text-left">
                  <span className="text-[12px] w-36 flex-shrink-0" style={{ color: C.text1 }}>{fmtDateTime(a.createdAtUtc)}</span>
                  <Badge label={auditActionLabels[a.action] || a.action} color={C.blue} />
                  <span className="text-[13px] flex-1 min-w-0 truncate" style={{ color: C.text2 }}>{a.summary}</span>
                  {a.targetTenantName && <span className="text-[12px]" style={{ color: C.text3 }}>{a.targetTenantName}</span>}
                  <span className="text-[12px] w-40 truncate flex-shrink-0" style={{ color: C.text3 }}>{a.actorEmail}</span>
                  <i className={`ti ti-chevron-${expanded === a.id ? "up" : "down"} text-xs flex-shrink-0`} style={{ color: C.text3 }} />
                </button>
                {expanded === a.id && (
                  <div className="border-t px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ borderColor: C.border, background: C.card2 }}>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: C.text3 }}>Entity</div>
                      <div className="text-[13px]" style={{ color: C.text1 }}>{a.entityType} · <span className="font-mono">{a.entityId}</span></div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: C.text3 }}>IP address</div>
                      <div className="text-[13px] font-mono" style={{ color: C.text2 }}>{a.ipAddress || "—"}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {meta && <Pagination page={meta.page} totalPages={meta.totalPages} hasPrevious={meta.hasPrevious} hasNext={meta.hasNext} onPage={setPage} />}
        </>
      )}
    </div>
  );
}

// ─── ADMIN USERS ── no backend endpoint exists yet — static preview only ──────
function UsersPage({ role }) {
  const [filter, setFilter] = useState("All");
  const roleColor = { SuperAdmin: C.accent, PlatformAdmin: C.blue, BillingAdmin: C.purple, SupportAdmin: C.green, ReadOnlyAdmin: C.text3 };
  const filtered = adminUsers.filter(u => filter === "All" || u.role === filter);

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <SectionHeader title="Admin Users" sub="Super admin team & role assignment" showAction={false} />
      <div className="rounded-xl border px-4 py-3 mb-4 text-[13px] flex items-center gap-2" style={{ background: `${C.yellow}12`, borderColor: `${C.yellow}30`, color: C.yellow }}>
        <i className="ti ti-alert-triangle text-sm flex-shrink-0" />
        No admin-users API exists in the backend yet — this list is static preview data and isn't connected.
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["All", ...ROLES].map(r => (
          <button key={r} onClick={() => setFilter(r)}
            className="text-[12px] font-medium rounded-full px-3 py-1.5 border transition-all"
            style={filter === r ? { background: `${roleColor[r] || C.accent}20`, color: roleColor[r] || C.accent, borderColor: `${roleColor[r] || C.accent}40` } : { background: C.card, color: C.text3, borderColor: "transparent" }}>
            {r}
          </button>
        ))}
      </div>
      <Table
        cols={[
          { key: "name", label: "Name", w: "1.4fr", render: u => (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0" style={{ background: roleColor[u.role] }}>{u.name.split(" ").map(n => n[0]).join("").slice(0, 2)}</div>
              <div><div className="text-[14px] font-semibold" style={{ color: C.text1 }}>{u.name}</div><div className="text-[12px]" style={{ color: C.text3 }}>{u.email}</div></div>
            </div>
          )},
          { key: "role", label: "Role", w: "120px", render: u => <Badge label={u.role} color={roleColor[u.role]} /> },
          { key: "status", label: "Status", w: "90px", render: u => (
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: u.status === "Active" ? C.green : C.text3 }} /><span className="text-[12px]" style={{ color: C.text3 }}>{u.status}</span></div>
          )},
          { key: "mfa", label: "MFA", w: "70px", render: u => <i className={`ti ${u.mfa ? "ti-shield-check" : "ti-shield-off"} text-xs`} style={{ color: u.mfa ? C.green : C.text3 }} /> },
          { key: "lastLogin", label: "Last login", w: "100px" },
        ]}
        rows={filtered}
      />
    </div>
  );
}

// ─── SETTINGS ── no super-admin platform settings endpoint exists yet ─────────
function SettingsPage() {
  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6">
      <SectionHeader title="Settings" sub="Platform configuration & integrations" showAction={false} />
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="ti ti-settings text-4xl mb-4" style={{ color: C.text3 }} />
          <div className="text-sm font-medium" style={{ color: C.text1 }}>Platform Settings</div>
          <div className="text-[12px] mt-1" style={{ color: C.text3 }}>Not in the backend API — placeholder for future config. (The tenant-level Settings API at /api/settings belongs to TenantAdmin, not this console.)</div>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function SuperAdminPanel({ onLogout }) {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [role, setRole] = useState("SuperAdmin");
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  // Custom setActive handler that checks for userDashboard navigation
  const handleSetActive = (pageId) => {
    if (pageId === "userDashboard") {
      navigate("/dashboard");
    } else {
      setActivePage(pageId);
    }
  };

  const [toasts, setToasts] = useState([]);
  function pushToast(message, type = "success") {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    
    // Auto-remove after 4 seconds (increased from 3.5s)
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
    
    // Add sound feedback for better UX
    try {
      // Create audio context for modern browsers
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different tones for success vs error
      oscillator.frequency.value = type === "error" ? 300 : 800;
      oscillator.type = "sine";
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Fallback for browsers that don't support audio
      console.log("Toast notification:", message);
    }
  }

  const pages = {
    dashboard: <DashboardPage setActivePage={setActivePage} setSelectedTenantId={setSelectedTenantId} />,
    tenants: <TenantsPage setActivePage={setActivePage} setSelectedTenantId={setSelectedTenantId} role={role} />,
    tenantDetail: <TenantDetailPage tenantId={selectedTenantId} role={role} setActivePage={setActivePage} />,
    plans: <PlansPage role={role} />,
    invoices: <InvoicesPage role={role} setActivePage={setActivePage} />,
    payments: <PaymentsPage setActivePage={setActivePage} />,
    audit: <AuditLogPage />,
    users: <UsersPage role={role} />,
    settings: <SettingsPage />,
  };

  return (
    <ToastContext.Provider value={pushToast}>
      <div className="flex h-screen w-screen overflow-hidden" style={{ background: C.bg }}>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css" />

        <Sidebar active={activePage} setActive={handleSetActive} role={role} />
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} active={activePage} setActive={handleSetActive} role={role} />

        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <Topbar page={activePage} onMenuOpen={() => setDrawerOpen(true)} role={role} setRole={setRole}
            setActivePage={handleSetActive} setSelectedTenantId={setSelectedTenantId} />
          <div className="flex flex-1 min-h-0 overflow-hidden" style={{ background: C.bg }}>
            {pages[activePage]}
          </div>
        </div>

        <MobileBottomNav active={activePage} setActive={handleSetActive} onMore={() => setDrawerOpen(true)} role={role} />
        <ToastStack toasts={toasts} />
      </div>
    </ToastContext.Provider>
  );
}